import Dexie, { Table } from 'dexie'
import { defineSchema } from './schema'
import { isSyncBookkeepingSuppressed, withoutSyncBookkeeping } from './syncBookkeeping'
import { createTaskCounter } from './taskCounters'
import { notifySyncQueued } from '../sync/sync-notify'
import {
  Task,
  Subtask,
  SubtaskDraft,
  Section,
  Setting,
  UserProfile,
  TimeEntry,
  TaskComment,
  ProjectComment,
  CommentDraft,
  TaskReminder,
  ReminderDraft,
  ReminderChanges,
  isStandardReminder,
  Tag,
  Project,
  Goal,
  Habit,
  HabitLog,
  RewardUsage,
  UserPreference,
  CanvasNote,
  LifeSpace,
  SyncMetadata,
  SyncQueueItem,
  TaskCascade,
  SubtaskTreeRow,
  ProjectCascade,
  ProjectLifeSpaceMove,
  CreateTask,
  UpdateSubtask,
  RecurrenceRule,
  NestOutcome,
  TaskPlacement,
  CreateSection,
  CreateSetting,
  CreateUserProfile,
  CreateTag,
  CreateProject,
  CreateGoal,
  CreateHabit,
  CreateCanvasNote,
  UpdateCanvasNote,
  UpdateTask,
  UpdateSection,
  UpdateUserProfile,
  UpdateProject,
  UpdateGoal,
  UpdateHabit,
  DB_NAME,
  DEFAULT_TASK_ATTENTION,
  DEFAULT_TASK_STATUS,
  DEFAULT_TASK_CATEGORY,
  DEFAULT_USER_PREFERENCES,
  DEFAULT_USER_STATISTICS,
  MIN_FOCUS_SECONDS
} from './models'
import { planIndent, planNest, planOutdent, planUnnest, sameList, type UnnestAt } from './taskNesting'
import { MAX_SUBTASK_DEPTH, treeEntriesOf, uniqueById } from './taskTree'
import { applyProjectMove, canHoldSubproject, flattenProjectTree, MAX_PROJECT_DEPTH, projectDepth, projectDescendantIds, projectSubtreeHeight, type ProjectTreeChange } from '../projectTree'
import { ancestorsCompletedBy } from './branchCompletion'
import { chainKeepsChildListed, isClosedByArchive, isPlannedWithinNextWeek, isProjectTask, isQuickNote, withoutSucceededRounds } from './taskFilters'
import { descendantsLosingRecurrence, reportChildRecurrenceDropped } from '../recurrence/childRecurrenceDropped'
import { hasUniqueOrder, orderBetween } from './subtaskOrder'
import { addDays } from '../date/calendar'
import { titleConsistencyChanges } from '../projectIcon/projectTitle'
import { STANDARD_REMINDER, gainsStartTime, reminderFromDraft } from '../reminders/reminderFromDraft'
import type { AvatarCategory, Avatar } from './types'

/*
  Der Suppress-Zähler und seine Klammern liegen in `syncBookkeeping.ts` — auch
  `schema.ts` braucht sie für die Migrationen, und diese Datei importiert
  `schema.ts`. Hier re-exportiert, weil der halbe Sync-Ordner sie von hier holt.
*/
export { withSyncWrite, withoutSyncBookkeeping } from './syncBookkeeping'

export class HappyTaskDB extends Dexie {
  tasks!: Table<Task>
  subtasks!: Table<Subtask>
  sections!: Table<Section>
  settings!: Table<Setting>
  userProfiles!: Table<UserProfile>
  timeEntries!: Table<TimeEntry>
  taskComments!: Table<TaskComment>
  projectComments!: Table<ProjectComment>
  taskReminders!: Table<TaskReminder>
  tags!: Table<Tag>
  projects!: Table<Project>
  goals!: Table<Goal>
  habits!: Table<Habit>
  habitLogs!: Table<HabitLog>
  rewardUsages!: Table<RewardUsage>
  userPreferences!: Table<UserPreference>
  canvasNotes!: Table<CanvasNote>
  syncMetadata!: Table<SyncMetadata>
  syncQueue!: Table<SyncQueueItem>
  avatarCategories!: Table<AvatarCategory>
  avatars!: Table<Avatar>

  constructor() {
    super(DB_NAME)

    // Version 2: Base schema
    defineSchema(this)

    // Hook to auto-update timestamps and sync fields
    this.tasks.hook('creating', (_primaryKey, obj) => {
      obj.id = obj.id || crypto.randomUUID()
      obj.order = obj.order ?? 0
      if (isSyncBookkeepingSuppressed()) return  // Sync engine or a device-local write (e.g. note-height measurement) provides/omits its own bookkeeping
      const now = new Date()
      obj.createdAt = now
      obj.updatedAt = now
      obj.lastModified = now
      obj.synced = false
      obj.syncedAt = null
    })

    this.tasks.hook('updating', (_modifications, _primaryKey, _obj, _trans) => {
      if (isSyncBookkeepingSuppressed()) return undefined  // Sync engine or a device-local write (e.g. note-height measurement) manages bookkeeping itself
      const now = new Date()
      // Dexie's `updating`-Hook wendet Mutationen an `modifications` NUR an,
      // wenn der Hook sie auch zurückgibt (der Parameter ist ein Delta-Objekt,
      // keine Referenz auf die Zeile — anders als bei `creating`). Ein reines
      // `modifications.x = y` ohne `return` ist ein stiller No-op: Der Aufruf
      // sieht aus, als würde er `synced`/die Zeitstempel setzen, tut es aber
      // nicht. Deshalb hier explizit zurückgeben.
      return {
        updatedAt: now,
        lastModified: now,
        synced: false,
      }
    })

    // Normalize tasks on read: ensure startDate/startTime/startPeriod are never undefined.
    // This handles objects written by db.tasks.put() (sync/undo) that may lack these fields.
    // Apply similar hooks to all tables
    const applyHooks = (table: Table) => {
      table.hook('creating', (_primaryKey, obj, _trans) => {
        obj.id = obj.id || crypto.randomUUID()
        if (isSyncBookkeepingSuppressed()) return  // Sync engine or a device-local write (e.g. note-height measurement) provides/omits its own bookkeeping
        const now = new Date()
        obj.createdAt = now
        obj.updatedAt = now
        obj.lastModified = now
        obj.synced = false
        obj.syncedAt = null
      })

      table.hook('updating', (_modifications, _primaryKey, _obj, _trans) => {
        if (isSyncBookkeepingSuppressed()) return undefined  // Sync engine or a device-local write (e.g. note-height measurement) manages bookkeeping itself
        const now = new Date()
        // Siehe Kommentar am `tasks`-Hook oben: Dexie übernimmt Mutationen an
        // `modifications` nur, wenn der Hook sie zurückgibt.
        return {
          updatedAt: now,
          lastModified: now,
          synced: false,
        }
      })
    }

    applyHooks(this.settings)
    applyHooks(this.userProfiles)
    applyHooks(this.timeEntries)
    applyHooks(this.tags)
    applyHooks(this.projects)
    applyHooks(this.sections)
    applyHooks(this.goals)
    applyHooks(this.habits)
    applyHooks(this.habitLogs)
    applyHooks(this.rewardUsages)
    applyHooks(this.userPreferences)
    applyHooks(this.subtasks)
    applyHooks(this.canvasNotes)
    applyHooks(this.taskComments)
    applyHooks(this.projectComments)
    applyHooks(this.taskReminders)
  }

  // Task operations
  async createTask({ subtasks: drafts = [], comments = [], reminders, ...taskData }: CreateTask & { id?: string }): Promise<Task> {
    const task: Task = {
      ...taskData,
      id: taskData.id ?? crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      attention: taskData.attention ?? DEFAULT_TASK_ATTENTION,
      status: taskData.status || DEFAULT_TASK_STATUS,
      category: taskData.category || DEFAULT_TASK_CATEGORY,
      lifeSpace: taskData.lifeSpace,
      projectId: taskData.projectId ?? null,
      sectionId: taskData.sectionId ?? null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      // Respect incoming pausedAt so duplicating a paused task keeps it
      // in Ruhend. Defaults to null for fresh tasks.
      pausedAt: taskData.pausedAt ?? null,
      // Carried through so duplicating a task keeps its reward.
      rewardId: taskData.rewardId ?? null,
      // Die Wiederholung reist beim Duplizieren und beim Nachfolger mit (ADR 0021).
      recurrence: taskData.recurrence ?? null,
      durationMode: taskData.durationMode ?? null,
      startDate: taskData.startDate ?? null,
      startTime: taskData.startTime ?? null,
      startPeriod: taskData.startPeriod ?? null,
      dueTime: taskData.dueTime ?? null,
      duePeriod: taskData.duePeriod ?? null,
      order: taskData.order ?? await this.getNextTaskOrder(taskData.projectId ?? null),
      // `null` = nie auf „Demnächst" gezogen. Beim Duplizieren reicht die
      // Quelle einen Wert herein, damit die Kopie neben dem Original landet.
      dayOrder: taskData.dayOrder ?? null,
      importedFrom: taskData.importedFrom ?? null,
    }
    
    await this.tasks.add(task)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'tasks', { localId: task.id })

    // Direkt hinter der Aufgabe, vor ihren Unteraufgaben: Bricht ein Import mittendrin ab,
    // fehlen einer übernommenen Aufgabe höchstens Unteraufgaben, nie ihre Kommentare (#182).
    for (const comment of comments) await this.createComment(task.id, comment)
    // Ohne mitgegebene Liste bekommt eine Aufgabe mit Uhrzeit die Standard-Erinnerung (`CreateTask.reminders`).
    for (const reminder of reminders ?? (gainsStartTime(null, task) ? [STANDARD_REMINDER] : [])) await this.createReminder(task.id, reminder)

    // The checklist typed before the task existed. Written here rather than at
    // each creation call site so no path can quietly drop it.
    for (let i = 0; i < drafts.length; i++) {
      await this.addChild(task.id, { ...drafts[i]!, order: i })
    }

    return task
  }

  async updateTask(id: string, updates: Partial<UpdateTask>): Promise<Task | null> {
    const before = updates.status !== undefined || updates.recurrence != null || updates.startTime !== undefined || updates.startDate !== undefined ? await this.tasks.get(id) : undefined
    await this.tasks.update(id, before ? { ...updates, ...this.taskCounter.count(before, updates) } : updates)
    const raw = await this.tasks.get(id)
    const updated = raw ? this.normalizeTask(raw) : null

    // Queue for cloud sync
    if (updated) {
      await this.queueSyncChange('update', 'tasks', { localId: id })
      await this.moveChildrenWithParent(id, updates)
      if (before && before.status !== updated.status) await this.coupleCompletion(updated)
      if (updates.recurrence != null) await this.dropChildRecurrence(id, before?.recurrence ?? null)
      if (before && gainsStartTime(before, updated)) await this.addStandardReminderIfMissing(id)
    }

    return updated
  }

  /**
   * Die **Standard-Erinnerung** für eine Aufgabe, die gerade eine Uhrzeit am Wann bekommen hat —
   * außer sie hat schon eine (Uhrzeit entfernt und wieder gesetzt: die alte ist noch da).
   */
  async addStandardReminderIfMissing(taskId: string): Promise<void> {
    const existing = await this.taskReminders.where('taskId').equals(taskId).filter(r => !r.deletedAt && isStandardReminder(r)).count()
    if (existing === 0) await this.createReminder(taskId, STANDARD_REMINDER)
  }

  /**
   * **Wird eine Aufgabe wiederkehrend, verlieren ihre Unteraufgaben ihren Rhythmus** (#205,
   * Punkt 5) — auf jeder Ebene alles darunter (#181) — und die Oberfläche erfährt es (`reportChildRecurrenceDropped`), um es zu sagen
   * und `⌘Z` anzubieten. `parentBefore`: die Wiederholung der Aufgabe vor dem Schreiben.
   */
  async dropChildRecurrence(parentId: string, parentBefore: RecurrenceRule | null): Promise<void> {
    const parent = await this.tasks.get(parentId)
    const losing = descendantsLosingRecurrence(parent, { recurrence: parent?.recurrence ?? null }, await this.getDescendantTasks(parentId))
    if (losing.length === 0) return
    for (const child of losing) {
      await this.tasks.update(child.id, { recurrence: null })
      await this.queueSyncChange('update', 'tasks', { localId: child.id })
    }
    reportChildRecurrenceDropped({
      parentId, parentBefore, children: losing.map(child => ({ id: child.id, recurrence: child.recurrence! })),
    })
  }

  /** Zeile → die Zeilen darunter, die ihr Abhaken in dieser Sitzung mitgenommen hat. */
  private coupledChildren = new Map<string, string[]>()

  /** **Erledigt-Zähler** und **Vertagen** (#198) — an jedem Schreibweg der Oberfläche, nie im Sync. */
  readonly taskCounter = createTaskCounter()

  /**
   * **Abhaken wie in Todoist** (T14 #179, Grill 5 — per Versuch im Todoist-Konto geprüft), auf
   * jeder Ebene gleich (#181, Grill 8): Eine Zeile abhaken hakt ihren ganzen offenen Ast mit ab;
   * eine Zeile wieder öffnen öffnet die Kette darüber bis zur Aufgabe, Geschwister bleiben. Das
   * letzte Kind abzuhaken schließt seit #217 die Eltern-Zeile mit, bis Ebene 1 — die **Aufgabe**
   * nie (`ancestorsCompletedBy`). Öffnet man eine Zeile wieder (Haken zurück, `⌘Z`), kommen
   * genau die mit, die ihr Abhaken genommen hat — nicht die, die vorher schon erledigt waren.
   */
  private async coupleCompletion(task: Task): Promise<void> {
    const set = async (id: string, done: boolean, at: Date | null) => {
      const changes: Partial<Task> = { status: done ? 'completed' : 'todo', completedAt: done ? at : null }
      const row = await this.tasks.get(id)
      // Mitgenommene Zeilen sind erledigt wie selbst abgehakte — sie zählen mit (#198).
      await this.tasks.update(id, row ? { ...changes, ...this.taskCounter.count(row, changes) } : changes)
      await this.queueSyncChange('update', 'tasks', { localId: id })
    }
    if (task.status === 'completed') {
      const open = (await this.getDescendantTasks(task.id)).filter(c => c.status !== 'completed')
      for (const row of open) await set(row.id, true, task.completedAt ?? new Date())
      // Immer neu setzen: Ein alter Eintrag (die Zeile ging per Sync wieder auf) öffnete sonst
      // beim nächsten Haken zurück eine Unteraufgabe, die man selbst abgehakt hat.
      this.coupledChildren.set(task.id, open.map(c => c.id))
      if (task.parentTaskId == null) return
      const chain = await this.getAncestorTasks(task.id)
      const top = chain[chain.length - 1]
      if (!top) return
      const tree = uniqueById([...chain, ...(await this.getDescendantTasks(top.id))])
      for (const id of ancestorsCompletedBy(tree, task.id)) {
        await set(id, true, task.completedAt ?? new Date())
        this.coupledChildren.set(id, [])
      }
      return
    }
    const taken = this.coupledChildren.get(task.id) ?? []
    this.coupledChildren.delete(task.id)
    for (const id of taken) {
      const row = await this.tasks.get(id)
      if (row && !row.deletedAt && row.status === 'completed') await set(id, false, null)
    }
    for (const ancestor of await this.getAncestorTasks(task.id)) {
      if (ancestor.deletedAt || ancestor.status !== 'completed') continue
      // Eine erledigte WIEDERKEHRENDE Zeile bleibt zu, und was über ihr steht auch: Ihre Runde hat
      // schon einen Nachfolger, und sie hier zu öffnen stünde neben ihm als zweite offene Kopie
      // (der Haken-Zurück-Weg, der den Nachfolger wegnimmt, liefe nicht mit).
      if (ancestor.recurrence) break
      this.coupledChildren.delete(ancestor.id)
      await set(ancestor.id, false, null)
    }
  }

  /**
   * **Unteraufgaben erben den Ort** ihrer Aufgabe (T14 #179): Zieht die Aufgabe in ein
   * anderes Projekt, einen anderen Abschnitt oder Lebensbereich, gehen sie mit — alle Ebenen (#181). Jeder
   * Schreibweg, der diese Felder an einer Aufgabe ändert, ruft das.
   */
  async moveChildrenWithParent(parentId: string, changes: Partial<Pick<Task, 'projectId' | 'sectionId' | 'lifeSpace'>>): Promise<void> {
    const location: Partial<Pick<Task, 'projectId' | 'sectionId' | 'lifeSpace'>> = {}
    if (changes.projectId !== undefined) location.projectId = changes.projectId
    if (changes.sectionId !== undefined) location.sectionId = changes.sectionId
    if (changes.lifeSpace !== undefined) location.lifeSpace = changes.lifeSpace
    if (Object.keys(location).length === 0) return
    for (const child of await this.getDescendantTasks(parentId)) {
      if (Object.entries(location).every(([key, value]) => child[key as keyof Task] === value)) continue
      await this.tasks.update(child.id, location)
      await this.queueSyncChange('update', 'tasks', { localId: child.id })
    }
  }

  /** **Einhängen** (#204): siehe {@link planNest}. Eine Transaktion — alle Zeilen oder keine. */
  async nestTasks(ids: string[], parentId: string, afterId?: string | null): Promise<NestOutcome> {
    return this.transaction('rw', this.tasks, this.syncQueue, async () => {
      const heads = (await this.tasks.bulkGet([parentId, ...ids])).filter((t): t is Task => t != null)
      const targetChildren = await this.getChildTasks(parentId)
      // Die Bäume, aus denen sie kommen: ihr Ast und ihre alten Runden ziehen mit (#181).
      const plan = planNest(uniqueById([...heads, ...targetChildren, ...await this.getTreesOf(ids)]), ids, parentId, afterId)
      if ('refused' in plan) return plan
      await this.writePlacements(plan.after)
      return { before: plan.before }
    })
  }

  /**
   * **Herauslösen** (#204): siehe {@link planUnnest} — ohne `at` direkt unter die Aufgabe ganz oben
   * in ihrer Kette (#181). `null`, wenn `id` keine Unteraufgabe ist.
   */
  async unnestTask(id: string, at: UnnestAt = 'below'): Promise<TaskPlacement[] | null> {
    return this.transaction('rw', this.tasks, this.sections, this.syncQueue, async () => {
      const task = await this.tasks.get(id)
      const top = task?.parentTaskId ? (await this.getAncestorTasks(id)).at(-1) : undefined
      const root = typeof at !== 'object' ? top : undefined
      const listed = root ? await this.tasks.filter(t => t.parentTaskId == null && !t.deletedAt && sameList(root, t)).toArray() : []
      const section = root?.sectionId ? await this.sections.get(root.sectionId) : undefined
      // Der Baum ihrer Aufgabe: ihr Ast und ihre alten Runden ziehen mit (#181).
      const family = top ? [top, ...await this.getDescendantTasks(top.id)] : []
      const plan = planUnnest(task, at, root, listed, section?.order, family)
      if (!plan) return null
      await this.writePlacements(plan.after)
      return plan.before
    })
  }

  /** **Einrücken** (⌥→, #181): siehe {@link planIndent}. Eine Transaktion — alle Zeilen oder keine. */
  async indentSubtasks(ids: string[]): Promise<NestOutcome> {
    return this.transaction('rw', this.tasks, this.syncQueue, async () => {
      const plan = planIndent(await this.getTreesOf(ids), ids)
      if ('refused' in plan) return plan
      await this.writePlacements(plan.after)
      return { before: plan.before }
    })
  }

  /** **Ausrücken** (⌥←, #181): siehe {@link planOutdent}. */
  async outdentSubtasks(ids: string[]): Promise<NestOutcome> {
    return this.transaction('rw', this.tasks, this.syncQueue, async () => {
      const plan = planOutdent(await this.getTreesOf(ids), ids)
      if ('refused' in plan) return plan
      await this.writePlacements(plan.after)
      return { before: plan.before }
    })
  }

  /**
   * Die Kette über `id`, vom Eltern-Teil hinauf bis zur Aufgabe (#181) — gelöschte Glieder
   * eingeschlossen, wie `ancestorsOf`. Hält bei einem Zyklus an.
   */
  async getAncestorTasks(id: string): Promise<Task[]> {
    const chain: Task[] = []
    const seen = new Set([id])
    let parentId = (await this.tasks.get(id))?.parentTaskId
    while (parentId != null && !seen.has(parentId)) {
      const parent = await this.tasks.get(parentId)
      if (!parent) break
      seen.add(parentId)
      chain.push(this.normalizeTask(parent))
      parentId = parent.parentTaskId
    }
    return chain
  }
  /**
   * Die Kette jeder Zeile in `ids` (#181): sie selbst, darüber alles bis zur Aufgabe — gelöschte
   * Glieder eingeschlossen, fehlende Zeilen fehlen. Liest Ebene um Ebene, geteilte Eltern-Teile
   * einmal. Hält bei einem Zyklus an.
   */
  async getTaskChains(ids: readonly string[]): Promise<Map<string, Task[]>> {
    const byId = new Map<string, Task>()
    let wanted = [...new Set(ids)]
    while (wanted.length > 0) {
      const rows = (await this.tasks.bulkGet(wanted)).filter((t): t is Task => t != null)
      for (const row of rows) byId.set(row.id, this.normalizeTask(row))
      wanted = [...new Set(rows.flatMap(t => (t.parentTaskId && !byId.has(t.parentTaskId) ? [t.parentTaskId] : [])))]
    }
    const chains = new Map<string, Task[]>()
    for (const id of ids) {
      const chain: Task[] = []
      const seen = new Set<string>()
      for (let row = byId.get(id); row && !seen.has(row.id); row = row.parentTaskId ? byId.get(row.parentTaskId) : undefined) {
        seen.add(row.id)
        chain.push(row)
      }
      if (chain.length > 0) chains.set(id, chain)
    }
    return chains
  }


  /** Alle lebenden Nachkommen von `id`, Ebene für Ebene geholt (#181); ordnen: `descendantsOf`. */
  async getDescendantTasks(id: string): Promise<Task[]> {
    const found: Task[] = []
    const seen = new Set([id])
    let level = [id]
    while (level.length > 0) {
      const rows = (await this.tasks.where('parentTaskId').anyOf(level).filter(t => !t.deletedAt).toArray())
        .filter(t => !seen.has(t.id))
      rows.forEach(t => seen.add(t.id))
      found.push(...rows.map(t => this.normalizeTask(t)))
      level = rows.map(t => t.id)
    }
    return found
  }

  /** Die Bäume der Aufgaben, unter denen `ids` stehen: jede Aufgabe samt allen Nachkommen. */
  private async getTreesOf(ids: readonly string[]): Promise<Task[]> {
    const roots = new Map<string, Task>()
    for (const id of ids) {
      const root = (await this.getAncestorTasks(id)).at(-1) ?? await this.tasks.get(id)
      if (root) roots.set(root.id, root)
    }
    const trees = await Promise.all([...roots.keys()].map(id => this.getDescendantTasks(id)))
    return uniqueById([...roots.values(), ...trees.flat()])
  }

  /** Rückweg von Einhängen, Herauslösen, Einrücken und Ausrücken: stellt die gemeldeten Plätze wieder her. */
  async restorePlacements(placements: TaskPlacement[]): Promise<void> {
    await this.transaction('rw', this.tasks, this.syncQueue, () => this.writePlacements(placements))
  }

  private async writePlacements(placements: TaskPlacement[]): Promise<void> {
    const moved = new Set(placements.map(p => p.id))
    // Eine Aufgabe, die ihr Elternteil wechselt, öffnet beim Haken-zurück des alten nicht mehr mit.
    for (const [parentId, taken] of this.coupledChildren) {
      this.coupledChildren.set(parentId, taken.filter(id => !moved.has(id)))
    }
    for (const { id, ...fields } of placements) {
      await this.tasks.update(id, fields)
      await this.queueSyncChange('update', 'tasks', { localId: id })
    }
  }

  /** Batch-update order (and optionally sectionId) for multiple tasks in a single transaction. */
  async reorderTasks(updates: Array<{ id: string; order: number; sectionId?: string | null }>): Promise<void> {
    const now = new Date()
    await this.transaction('rw', this.tasks, this.syncQueue, async () => {
      for (const { id, order, sectionId } of updates) {
        const fields: Partial<Task> = {
          order,
          updatedAt: now,
          lastModified: now,
          synced: false,
        }
        if (sectionId !== undefined) fields.sectionId = sectionId
        await this.tasks.update(id, fields)
        await this.queueSyncChange('update', 'tasks', { localId: id })
        if (sectionId !== undefined) await this.moveChildrenWithParent(id, { sectionId })
      }
    })
  }

  /**
   * Soft-deletes a task and cascades to its subtasks **and to the
   * Canvas-Elemente that point at it**.
   *
   * Postgres' ON DELETE CASCADE never fires for us — the app deletes via
   * `deleted_at`, never with a real DELETE — so the children have to be taken
   * here, each with its own queue entry (sync checklist point 16).
   *
   * Dasselbe gilt für `canvas_notes.task_id REFERENCES tasks(id) ON DELETE SET
   * NULL`: Die Regel steht in der Migration, greift aber nie. Ohne sie hier
   * bliebe eine Notiz mit einer **Verbindung** ins Leere zurück — sein
   * **Eselsohr** stünde weiter, und er wäre für immer nicht mehr übernehmbar
   * (CONTEXT.md, „Verbindung").
   *
   * `convertedAt`/`convertedContent` bleiben dabei stehen: Sie werden ohnehin
   * nur gelesen, solange `taskId` gesetzt ist, und so genügt dem Rückweg das
   * Zurücksetzen des einen Feldes.
   *
   * @returns die Kennungen, die dieser Aufruf genommen hat — Subtasks für
   *   {@link restoreTaskSubtasks}, Canvas-Elemente für
   *   {@link relinkCanvasNotes}. Kennungen statt eines Zeitstempels: Ein
   *   Subtask, den der User einen Moment vorher von Hand gelöscht hat, kann
   *   dieselbe Millisekunde tragen, und das Rückgängig darf ihn nicht mit
   *   wiederbeleben.
   */
  async deleteTask(id: string): Promise<TaskCascade> {
    const task = await this.tasks.get(id)
    const deletedAt = new Date()
    await this.tasks.update(id, { deletedAt })

    // **Die Fokuszeit geht mit.** Ein laufender Abschnitt wird vorher gebucht:
    // Die Aufgabe ist weg, aber die Zeit ist vergangen, und eine laufende Zeile
    // ohne Aufgabe hätte niemand je beendet.
    await this.closeRunningFocusOfTask(id, deletedAt)
    const focusEntries = await this.timeEntries
      .where('taskId')
      .equals(id)
      .filter(e => !e.deletedAt)
      .toArray()
    for (const entry of focusEntries) {
      await this.timeEntries.update(entry.id, { deletedAt })
      await this.queueSyncChange('delete', 'timeEntries', { localId: entry.id, cloudId: entry.cloudId })
    }

    // **Die Kommentare gehen mit** (#182) — sonst stünden sie lebend an einer toten
    // Aufgabe in der Cloud, und ein zweites Gerät könnte sie nie zuordnen.
    const commentIds = await this.deleteCommentsOfTasks([id], deletedAt)
    // Die Erinnerungen ebenso — eine tote Aufgabe erinnert nicht, und ein zweites Gerät fände sie nie.
    const reminderIds = await this.deleteRemindersOfTasks([id], deletedAt)

    // Die Unteraufgaben gehen mit — jede wie eine Aufgabe, samt ihrer Fokuszeit.
    const children = await this.getChildTasks(id)
    // Ihre gelösten Pinnwand-Verbindungen reisen nicht mit zurück: `relinkCanvasNotes`
    // hängt an die Kennung des Elternteils (Unteraufgaben entstehen nicht auf der Pinnwand).
    const childTimeEntryIds: string[] = []
    // Der ganze Ast (#181): Enkel meldet der Aufruf am Kind, der Rückweg braucht sie alle.
    const branchIds: string[] = []
    for (const child of children) {
      const cascade = await this.deleteTask(child.id)
      branchIds.push(child.id, ...cascade.subtaskIds)
      childTimeEntryIds.push(...(cascade.timeEntryIds ?? []))
      commentIds.push(...cascade.commentIds)
      reminderIds.push(...(cascade.reminderIds ?? []))
    }

    // Die Verbindung lösen, nicht die Notiz löschen: Er ist ein Einfall und
    // steht für sich, auch ohne die Aufgabe, die einmal aus ihm wurde.
    // Gelöschte Notizen bleiben unangetastet — ihre Verbindung interessiert
    // niemanden mehr, und ein Rückgängig soll sie nicht zurückholen.
    const linkedNotes = await this.canvasNotes
      .where('taskId')
      .equals(id)
      .filter(n => !n.deletedAt)
      .toArray()
    for (const note of linkedNotes) {
      await this.canvasNotes.update(note.id, { taskId: null })
      await this.queueSyncChange('update', 'canvasNotes', { localId: note.id })
    }

    // Queue for cloud sync
    await this.queueSyncChange('delete', 'tasks', { localId: id, cloudId: task?.cloudId })
    return {
      subtaskIds: branchIds,
      unlinkedNoteIds: linkedNotes.map(n => n.id),
      timeEntryIds: [...focusEntries.map(e => e.id), ...childTimeEntryIds],
      commentIds,
      reminderIds,
    }
  }

  /**
   * Bucht den laufenden Abschnitt, WENN er zu dieser Aufgabe gehört — für die
   * Kaskade in {@link deleteTask}. Läuft der Fokus woanders, bleibt er stehen:
   * Wer nebenher eine fremde Aufgabe löscht, soll dabei nicht seinen Fokus
   * verlieren.
   */
  private async closeRunningFocusOfTask(taskId: string, at: Date): Promise<void> {
    const running = await this.getRunningFocus()
    if (running?.taskId !== taskId) return
    await this.closeRunningFocus(at)
  }

  /**
   * Hängt die **Verbindung** wieder an — das Gegenstück zum Lösen in
   * {@link deleteTask}, gerufen aus dem „Rückgängig" des Lösch-Hinweises.
   *
   * Genau die gemeldeten Notizen, nicht „alle mit diesem convertedAt": Wer
   * einen Zettel kurz vorher selbst vom Ohr befreit hat, soll ihn nicht durch
   * ein fremdes Rückgängig zurückbekommen.
   */
  async relinkCanvasNotes(noteIds: string[], taskId: string): Promise<void> {
    for (const noteId of noteIds) {
      const note = await this.canvasNotes.get(noteId)
      if (!note) continue
      await this.canvasNotes.update(noteId, { taskId })
      await this.queueSyncChange('update', 'canvasNotes', { localId: noteId })
    }
  }

  /**
   * Holt weich gelöschte Aufgaben zurück — gebraucht vom **Rückgängig** des
   * Canvas-Stapels, das die Aufgaben einer **Übernahme** mitnimmt und beim
   * Wiederherstellen wiederbringt (ADR 0011).
   *
   * Gebaut wie {@link restoreTaskSubtasks}: Eine Zeile, die die Cloud nie
   * gesehen hat, braucht ein `create` und nicht das Update von nichts.
   */
  async restoreTasks(taskIds: string[]): Promise<void> {
    for (const id of taskIds) {
      const task = await this.tasks.get(id)
      if (!task) continue
      await this.tasks.update(id, { deletedAt: null })
      await this.queueSyncChange(
        task.cloudId ? 'update' : 'create',
        'tasks',
        { localId: id, cloudId: task.cloudId },
      )
    }
  }

  /** Ensure task objects always have startDate/startTime/startPeriod as null (not undefined).
   *  Needed because db.tasks.put() from sync/undo can strip fields absent from the written object. */
  private normalizeTask(task: Task): Task {
    let normalized = task
    if (!Reflect.has(normalized, 'startDate')) {
      normalized = { ...normalized, startDate: null, startTime: null, startPeriod: null }
    }
    if (!Reflect.has(normalized, 'dueTime')) {
      normalized = { ...normalized, dueTime: null, duePeriod: null }
    }
    if (!Reflect.has(normalized, 'pausedAt')) {
      normalized = { ...normalized, pausedAt: null }
    }
    if (!Reflect.has(normalized, 'recurrence')) {
      normalized = { ...normalized, recurrence: null }
    }
    return normalized
  }

  async getTask(id: string): Promise<Task | null> {
    const task = await this.tasks.get(id)
    return task ? this.normalizeTask(task) : null
  }

  async getAllTasks(): Promise<Task[]> {
    const tasks = await this.tasks.orderBy('order').filter(t => !t.deletedAt).toArray()
    return tasks.map(t => this.normalizeTask(t))
  }

  async getTasksByStatus(status: Task['status']): Promise<Task[]> {
    const tasks = await this.tasks.where('status').equals(status).toArray()
    return tasks.map(t => this.normalizeTask(t))
  }

  async getTasksByCategory(category: Task['category']): Promise<Task[]> {
    const tasks = await this.tasks.where('category').equals(category).toArray()
    return tasks.map(t => this.normalizeTask(t))
  }

  async getTasksByProject(projectId: string): Promise<Task[]> {
    const tasks = await this.tasks
      .where('projectId')
      .equals(projectId)
      .filter(t => isProjectTask(t, projectId))
      .sortBy('order')
    return tasks.map(t => this.normalizeTask(t))
  }

  /**
   * Tasks that belong in "Kurz notiert" for a given lifespace:
   *   projectId == null && lifeSpace == X
   *   && pausedAt == null && startDate == null && !deletedAt
   *
   * Added in PR 1 of the `Task.lifeSpace` migration — unused until PR 2
   * repoints QuickNotesPage away from the `quick-notes` system project.
   */
  async getQuickNotesByLifeSpace(
    lifeSpace: 'personal' | 'work' | 'home',
  ): Promise<Task[]> {
    const tasks = await this.tasks
      .where('lifeSpace')
      .equals(lifeSpace)
      // Erledigte kommen MIT: Sie liegen in Quick Notes hinter dem
      // **Geschafft-Schalter** (CONTEXT.md), nicht außerhalb der Abfrage —
      // sonst wäre eine abgehakte Notiz aus der Oberfläche verschwunden.
      .filter(isQuickNote)
      .sortBy('order')
    return tasks.map(t => this.normalizeTask(t))
  }

  /**
   * Count for the sidebar's "Demnächst" badge: tasks of one lifespace due
   * strictly after today and within the next seven days.
   *
   * Excludes paused tasks (they live under "Ruhend" only), completed and
   * soft-deleted ones. Today itself belongs to "Mein Tag", not here.
   */
  async getUpcomingTaskCountByLifeSpace(
    lifeSpace: 'personal' | 'work' | 'home',
  ): Promise<number> {
    return (await this.getPlannedThisWeek(lifeSpace)).length
  }

  /**
   * Was in der Woche eingeplant ist (Starttag heute bis +7, `isPlannedWithinNextWeek`) — über den
   * Index `startDate`: Gelesen werden nur diese Aufgaben, nicht jede Aufgabe eines Lebensbereichs (#309).
   * Ohne `lifeSpace` alle drei Bereiche. Alle Schreibwege legen `startDate` als `Date` ab.
   */
  async getPlannedThisWeek(lifeSpace?: LifeSpace): Promise<Task[]> {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 8)
    const planned = await this.tasks
      .where('startDate')
      .between(from, to, true, false)
      .filter(t => (!lifeSpace || t.lifeSpace === lifeSpace) && isPlannedWithinNextWeek(t, now))
      .toArray()
    // Dieselbe Regel wie die Seite: Unteraufgaben nur, solange jede Ebene darüber offen und wach ist.
    const parentIds = [...new Set(planned.flatMap(t => (t.parentTaskId ? [t.parentTaskId] : [])))]
    if (parentIds.length === 0) return planned
    const chains = await this.getTaskChains(parentIds)
    return planned.filter(t => !t.parentTaskId || chainKeepsChildListed(chains.get(t.parentTaskId)))
  }

  /** Ob Kurz notiert eines Bereichs etwas enthält — liest bis zum ersten Treffer statt der ganzen Liste (#309). */
  async hasQuickNote(lifeSpace: LifeSpace): Promise<boolean> {
    return (await this.tasks.where('lifeSpace').equals(lifeSpace).filter(isQuickNote).first()) !== undefined
  }

  /** Zahl bei „Demnächst“ und Kurz-notiert-Symbol aller drei Bereiche in einem Lauf — die Seitenleiste (#309). */
  async getSidebarCounts(): Promise<Record<LifeSpace, { upcomingCount: number; quickNotesHasEntries: boolean }>> {
    const [planned, personal, work, home] = await Promise.all([
      this.getPlannedThisWeek(),
      this.hasQuickNote('personal'),
      this.hasQuickNote('work'),
      this.hasQuickNote('home'),
    ])
    const upcoming = (space: LifeSpace) => planned.filter(t => t.lifeSpace === space).length
    return {
      personal: { upcomingCount: upcoming('personal'), quickNotesHasEntries: personal },
      work: { upcomingCount: upcoming('work'), quickNotesHasEntries: work },
      home: { upcomingCount: upcoming('home'), quickNotesHasEntries: home },
    }
  }

  async completeTask(id: string): Promise<Task | null> {
    const now = new Date()
    await this.tasks.update(id, {
      status: 'completed',
      completedAt: now,
    })
    // Queue for cloud sync — without this, the next pull cycle would
    // overwrite the local completion with the stale cloud copy
    // (status=todo) because no syncQueue entry tells the engine that
    // this row needs to be pushed first.
    await this.queueSyncChange('update', 'tasks', { localId: id })
    return await this.getTask(id)
  }

  /**
   * Ans Ende: hinter die größte Nummer aller Aufgaben — und hinter die Abschnitte des eigenen
   * Projekts (#276). Ohne die bekam eine Aufgabe, die gleich nach ihrem Abschnitt angelegt wurde
   * (Testdaten), dessen Nummer; die Liste sortierte sie dann vor den Kopf, in die Gruppe davor.
   */
  private async getNextTaskOrder(projectId: string | null): Promise<number> {
    const lastTask = await this.tasks.orderBy('order').last()
    const sections = projectId ? await this.sections.where('projectId').equals(projectId).toArray() : []
    return Math.max(lastTask?.order || 0, ...sections.map(section => section.order)) + 1
  }

  // Setting operations
  async createSetting(settingData: CreateSetting): Promise<Setting> {
    const setting: Setting = {
      ...settingData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null
    }
    
    await this.settings.add(setting)
    return setting
  }

  async getSetting(key: string): Promise<Setting | null> {
    return await this.settings.where('key').equals(key).first() || null
  }

  async getSettingValue<T = any>(key: string, defaultValue?: T): Promise<T> {
    const setting = await this.getSetting(key)
    return (setting ? setting.value : defaultValue) as T
  }

  async setSetting(key: string, value: any, type: Setting['type'] = 'string', description?: string): Promise<Setting> {
    const existing = await this.getSetting(key)
    
    if (existing) {
      await this.settings.update(existing.id, { value, type, description })
      return await this.getSetting(key) as Setting
    } else {
      return await this.createSetting({
        key,
        value,
        type,
        description: description || null,
        isUserSetting: true
      })
    }
  }

  async getAllSettings(): Promise<Setting[]> {
    return await this.settings.toArray()
  }

  // User Profile operations
  async createUserProfile(profileData: CreateUserProfile): Promise<UserProfile> {
    const profile: UserProfile = {
      ...profileData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      usagePurposes: profileData.usagePurposes || [],
      userIntents: profileData.userIntents || [],
      preferences: { ...DEFAULT_USER_PREFERENCES, ...profileData.preferences },
      statistics: { ...DEFAULT_USER_STATISTICS, ...profileData.statistics }
    }

    await this.userProfiles.add(profile)
    return profile
  }

  async getCurrentUserProfile(): Promise<UserProfile | null> {
    return await this.userProfiles.orderBy('createdAt').first() || null
  }

  async updateUserProfile(id: string, updates: Partial<UpdateUserProfile>): Promise<UserProfile | null> {
    await this.userProfiles.update(id, updates)
    return await this.userProfiles.get(id) || null
  }

  // ---- Fokuszeit (ADR 0025) ----

  /**
   * **Der laufende Abschnitt** — der eine mit `endTime === null`, sonst `null`.
   *
   * Es reicht, die JÜNGSTE Zeile anzusehen: {@link startFocus} beendet in
   * derselben Transaktion den vorigen Abschnitt, bevor es einen neuen anlegt,
   * und aus der Cloud kommen nur beendete. Läuft etwas, ist es also immer der
   * neueste Eintrag — und sonst läuft nichts.
   *
   * Über `startTime` und nicht über `endTime`: `where('endTime').equals(null)`
   * stand hier bis 2026-09-16 und konnte nie etwas finden, weil IndexedDB
   * `null` nicht indiziert (Dexie wirft darauf sogar).
   */
  async getRunningFocus(): Promise<TimeEntry | null> {
    const newest = await this.timeEntries.orderBy('startTime').reverse().first()
    if (!newest || newest.endTime !== null || newest.deletedAt) return null
    return newest
  }

  /**
   * **Play** — beginnt einen Abschnitt Fokuszeit an dieser Aufgabe.
   *
   * Läuft schon einer (auch an derselben Aufgabe), wird er gebucht: **ein Fokus
   * zur Zeit**. Beides in einer Transaktion, damit nie zwei laufende Zeilen
   * nebeneinander stehen.
   *
   * **Der neue Abschnitt wird nicht eingereiht.** Ein laufender Fokus bleibt auf
   * dem Gerät (ADR 0025); hochgeladen wird er erst, wenn er endet. Der beendete
   * Vorgänger reiht sich dabei ganz normal ein.
   */
  async startFocus(taskId: string): Promise<TimeEntry> {
    const now = new Date()
    const entry: TimeEntry = {
      id: crypto.randomUUID(),
      taskId,
      startTime: now,
      endTime: null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
    }
    await this.transaction('rw', this.timeEntries, this.syncQueue, async () => {
      await this.closeRunningFocus(now)
      await this.timeEntries.add(entry)
    })
    return entry
  }

  /**
   * **Pause, Haken, Zuklappen** — bucht den laufenden Abschnitt. Läuft keiner,
   * geschieht nichts.
   *
   * `at` ist für die Fälle, in denen der Fokus später bemerkt wird als er
   * endete: Zuklappen und Sperren melden ihren Zeitpunkt nach, und nach einem
   * Absturz zählt das letzte Lebenszeichen (3b).
   */
  async endFocus(at: Date = new Date()): Promise<TimeEntry | null> {
    return this.transaction('rw', this.timeEntries, this.syncQueue, () => this.closeRunningFocus(at))
  }

  /**
   * **Nachgetragene Fokuszeit** — ein schon beendeter Abschnitt (Testdaten). Der laufende bleibt
   * unberührt; die Zeile reiht sich als `create` ein wie ein gebuchter Abschnitt.
   */
  async addTimeEntry(taskId: string, startTime: Date, endTime: Date): Promise<TimeEntry> {
    const now = new Date()
    const entry: TimeEntry = {
      id: crypto.randomUUID(), taskId, startTime, endTime, userId: null, cloudId: null, deletedAt: null,
      createdAt: now, updatedAt: now, lastModified: now, synced: false, syncedAt: null,
    }
    await this.transaction('rw', this.timeEntries, this.syncQueue, async () => {
      await this.timeEntries.add(entry)
      await this.queueSyncChange('create', 'timeEntries', { localId: entry.id })
    })
    return entry
  }

  /**
   * Bucht den laufenden Abschnitt auf `at` — das gemeinsame Innere von
   * {@link endFocus}, {@link startFocus} und der Kaskade in {@link deleteTask}.
   * Muss in einer `rw`-Transaktion über `timeEntries` und `syncQueue` laufen.
   *
   * **Zu kurz wird verworfen, nicht gebucht.** Wer Play trifft und sofort
   * wieder Pause, hat sich verklickt; eine Zeile über zwei Sekunden wäre
   * Rauschen in der Fokuszeit. Dasselbe gilt, wenn `at` vor dem Beginn liegt
   * (eine nachgereichte Zeit aus einer krummen Uhr).
   */
  private async closeRunningFocus(at: Date): Promise<TimeEntry | null> {
    const running = await this.getRunningFocus()
    if (!running) return null

    const seconds = (at.getTime() - running.startTime.getTime()) / 1000
    if (seconds < MIN_FOCUS_SECONDS) {
      await this.timeEntries.delete(running.id)
      return null
    }

    await this.timeEntries.update(running.id, { endTime: at })
    // `create`: Die Zeile war nie oben — sie ist erst jetzt fertig.
    await this.queueSyncChange('create', 'timeEntries', { localId: running.id })
    return { ...running, endTime: at }
  }

  /**
   * **Gebuchte Sekunden je Aufgabe** — nur beendete Abschnitte. Den laufenden
   * Anteil rechnet die Oberfläche selbst dazu (`jetzt − startTime`), sonst
   * feuerte die Live-Query im Sekundentakt.
   *
   * Jede genannte Aufgabe steht in der Karte, auch mit `0` — so muss niemand
   * zwischen „noch nicht geladen" und „nie fokussiert" raten.
   */
  async getFocusSecondsByTask(taskIds: string[]): Promise<Map<string, number>> {
    const seconds = new Map<string, number>()
    for (const id of taskIds) seconds.set(id, 0)
    if (taskIds.length === 0) return seconds

    const entries = await this.timeEntries.where('taskId').anyOf(taskIds).toArray()
    for (const entry of entries) {
      if (!entry.endTime || entry.deletedAt) continue
      const elapsed = (entry.endTime.getTime() - entry.startTime.getTime()) / 1000
      seconds.set(entry.taskId, (seconds.get(entry.taskId) ?? 0) + Math.max(0, Math.round(elapsed)))
    }
    return seconds
  }

  /**
   * Undo-Gegenstück zur Fokuszeit-Kaskade in {@link deleteTask}: holt genau die
   * Abschnitte zurück, die dieser Aufruf genommen hat.
   *
   * Der Abschnitt, der beim Löschen noch LIEF, kommt als beendeter zurück — die
   * Zeit bis zum Löschen ist ja tatsächlich vergangen, und ein Rückgängig
   * startet keinen Fokus von selbst.
   */
  async restoreTaskTimeEntries(timeEntryIds: string[]): Promise<void> {
    for (const id of timeEntryIds) {
      const entry = await this.timeEntries.get(id)
      if (!entry) continue
      await this.timeEntries.update(id, { deletedAt: null })
      // Eine Zeile, die die Cloud nie gesehen hat, braucht ein `create`.
      await this.queueSyncChange(
        entry.cloudId ? 'update' : 'create',
        'timeEntries',
        { localId: id, cloudId: entry.cloudId },
      )
    }
  }

  // Tag operations
  async createTag(tagData: CreateTag): Promise<Tag> {
    const tag: Tag = {
      ...tagData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      usageCount: tagData.usageCount || 0,
      userId: null,
      cloudId: null,
      deletedAt: null,
      importedFrom: tagData.importedFrom ?? null,
    }

    await this.tags.add(tag)
    await this.queueSyncChange('create', 'tags', { localId: tag.id })
    return tag
  }

  async getTag(tagId: string): Promise<Tag | undefined> {
    return await this.tags.get(tagId)
  }

  async getAllTags(): Promise<Tag[]> {
    return await this.tags.orderBy('name').toArray()
  }

  async updateTag(id: string, updates: Partial<Omit<Tag, 'id'>>): Promise<void> {
    await this.tags.update(id, updates)
    await this.queueSyncChange('update', 'tags', { localId: id })
  }

  async deleteTag(id: string): Promise<void> {
    const tag = await this.tags.get(id)
    await this.tags.update(id, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'tags', { localId: id, cloudId: tag?.cloudId })
  }

  async getOrCreateTag(name: string, color?: string): Promise<Tag> {
    const existing = await this.tags.where('name').equals(name).first()

    if (existing) {
      return existing
    }

    return await this.createTag({
      name,
      color: color || '#3B82F6',
      description: null
    })
  }

  // Project operations
  async createProject({ comments = [], parentId = null, ...projectData }: CreateProject): Promise<Project> {
    const id = crypto.randomUUID()
    const placement = parentId ? await this.subprojectPlacement(id, parentId, projectData.lifeSpace) : null
    const project: Project = {
      ...projectData,
      id,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      userId: projectData.userId ?? null,
      cloudId: null,
      deletedAt: null,
      order: placement?.order ?? projectData.order ?? await this.getMinProjectOrder(projectData.lifeSpace) - 1,
      parentId: placement?.parentId ?? null,
      isSystem: projectData.isSystem ?? false,
      systemKind: projectData.systemKind ?? null,
      canvasPanelState: projectData.canvasPanelState ?? null,
      canvasSplitRatio: projectData.canvasSplitRatio ?? null,
      icon: projectData.icon ?? null,
      iconColor: projectData.iconColor ?? null,
      nameHtml: projectData.nameHtml ?? null,
      importedFrom: projectData.importedFrom ?? null,
    }

    await this.projects.add(project)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'projects', { localId: project.id })
    if (placement) await this.writeProjectTreeChanges(placement.others)

    // Direkt hinter dem Projekt: Bricht ein Import mittendrin ab, fehlen einem übernommenen
    // Projekt nie seine Kommentare — ein zweiter Import überspränge es samt ihnen (#184).
    for (const comment of comments) await this.createProjectComment(project.id, comment)

    return project
  }

  /**
   * Wo ein neues **Unterprojekt** hinkommt (#185): als letztes unter `parentId`, und der
   * Lebensbereich wird dahinter neu durchnummeriert. Steht das Elternprojekt auf Ebene 4, rückt
   * das neue direkt hinter es (Todoist-Import: tiefere Projekte rücken hoch). Ein Elternprojekt,
   * das fehlt oder in einem anderen Lebensbereich liegt, zählt nicht — dann `null`, oben.
   */
  private async subprojectPlacement(
    id: string,
    parentId: string,
    lifeSpace: string,
  ): Promise<{ order: number; parentId: string | null; others: ProjectTreeChange[] } | null> {
    const projects = await this.getProjectsByLifeSpace(lifeSpace)
    if (!projects.some(p => p.id === parentId)) return null
    const rows = flattenProjectTree(projects)
    let parent: string | null = parentId
    let afterId: string | null = null
    if (!canHoldSubproject(projects, parentId)) {
      afterId = parentId
      parent = rows.find(r => r.id === parentId)?.parentId ?? null
    } else {
      const children = rows.filter(r => r.parentId === parentId)
      afterId = children.at(-1)?.id ?? null
    }
    const changes = applyProjectMove([...projects, { id, order: Number.MAX_SAFE_INTEGER, parentId: null }], id, parent, afterId)
    const own = changes.find(c => c.id === id)
    if (!own) return null
    return { order: own.order, parentId: own.parentId, others: changes.filter(c => c.id !== id) }
  }

  /** Schreibt neue Plätze im Baum (`order`, `parentId`) und reiht jede Zeile ein. */
  private async writeProjectTreeChanges(changes: readonly ProjectTreeChange[]): Promise<void> {
    for (const { id, order, parentId } of changes) {
      await this.projects.update(id, { order, parentId })
      await this.queueSyncChange('update', 'projects', { localId: id })
    }
  }

  /**
   * **Ein Projekt im Baum verschieben** (#185, Sidebar): unter `parentId`, direkt hinter das
   * Geschwister `afterId` (`null` = als erstes Kind). Der Lebensbereich wird neu durchnummeriert;
   * geschrieben und eingereiht werden nur geänderte Zeilen. `false`, wenn der Zug nicht geht —
   * Elternprojekt im eigenen Ast oder in einem anderen Lebensbereich, oder der Ast ragte über
   * Ebene 4.
   */
  async moveProjectInTree(projectId: string, target: { parentId: string | null; afterId: string | null }): Promise<boolean> {
    return this.transaction('rw', this.projects, this.syncQueue, async () => {
      const project = await this.projects.get(projectId)
      if (!project || project.deletedAt) return false
      const projects = await this.getProjectsByLifeSpace(project.lifeSpace)
      if (target.parentId !== null) {
        if (!projects.some(p => p.id === target.parentId)) return false
        if (target.parentId === projectId || projectDescendantIds(projects, projectId).includes(target.parentId)) return false
        if (projectDepth(projects, target.parentId) + 1 + projectSubtreeHeight(projects, projectId) > MAX_PROJECT_DEPTH) return false
      }
      const changes = applyProjectMove(projects, projectId, target.parentId, target.afterId)
      await this.writeProjectTreeChanges(changes)
      return true
    })
  }

  async updateProject(id: string, updates: Partial<UpdateProject>): Promise<Project | null> {
    await this.projects.update(id, {
      ...titleConsistencyChanges(await this.projects.get(id), updates),
      ...updates,
      updatedAt: new Date(),
    })
    const updated = await this.projects.get(id) || null

    // Queue for cloud sync
    if (updated) {
      await this.queueSyncChange('update', 'projects', { localId: id })
    }

    return updated
  }

  /** Die lebenden Unterprojekte unter `projectId`, beliebig tief — über den Index, ohne den Lebensbereich. */
  private async liveSubprojectIds(projectId: string): Promise<string[]> {
    const out: string[] = []
    const queue = [projectId]
    const seen = new Set(queue)
    while (queue.length > 0) {
      const parent = queue.shift()!
      for (const child of await this.projects.where('parentId').equals(parent).filter(p => !p.deletedAt).toArray()) {
        if (seen.has(child.id)) continue
        seen.add(child.id)
        out.push(child.id)
        queue.push(child.id)
      }
    }
    return out
  }

  /**
   * **Nichts darin** (#263): keine Aufgabe, kein Abschnitt, kein Unterprojekt, kein Pinnwand-Element,
   * kein Kommentar. Rückgängig nach „Neues Projekt“ beim **Verschieben** nimmt das Projekt nur dann
   * wieder weg — was jemand inzwischen hineingelegt hat, bleibt.
   */
  async isProjectEmpty(id: string): Promise<boolean> {
    const live = (row: { deletedAt?: Date | null }) => !row.deletedAt
    const counts = await Promise.all([
      this.tasks.where('projectId').equals(id).filter(live).count(),
      this.sections.where('projectId').equals(id).filter(live).count(),
      this.projects.where('parentId').equals(id).filter(live).count(),
      this.canvasNotes.where('projectId').equals(id).filter(live).count(),
      this.projectComments.where('projectId').equals(id).filter(live).count(),
    ])
    return counts.every(count => count === 0)
  }

  /**
   * Weich löschen samt Kaskade — und **melden, was mitgegangen ist**
   * ({@link ProjectCascade}). Nur damit kann {@link restoreProject} genau das
   * zurückholen und alles liegen lassen, was der Nutzer vorher selbst gelöscht
   * hat. Dasselbe Muster wie {@link deleteTask}.
   *
   * **Der ganze Ast geht mit** (#185): jedes Unterprojekt samt seinen Abschnitten, Aufgaben
   * und Pinnwand-Elementen.
   */
  async deleteProject(id: string): Promise<ProjectCascade> {
    const subprojectIds = await this.liveSubprojectIds(id)
    const cascade: ProjectCascade = { taskIds: [], sectionIds: [], canvasNoteIds: [], commentIds: [], projectCommentIds: [], reminderIds: [], subprojectIds }
    // Kinder zuerst: Bricht es mittendrin ab, steht nie ein lebendes Kind unter einem toten Elternprojekt.
    for (const projectId of [...subprojectIds].reverse().concat(id)) {
      const one = await this.deleteOneProject(projectId)
      cascade.taskIds.push(...one.taskIds)
      cascade.sectionIds.push(...one.sectionIds)
      cascade.canvasNoteIds.push(...one.canvasNoteIds)
      cascade.commentIds.push(...one.commentIds)
      cascade.projectCommentIds!.push(...(one.projectCommentIds ?? []))
      cascade.reminderIds!.push(...(one.reminderIds ?? []))
    }
    return cascade
  }

  /**
   * Die Unterprojekte eines Projekts, das auf einem anderen Gerät gelöscht wurde (#185): Der Pull
   * (`afterRemoteDelete`) nimmt sie mit, samt allem, was an ihnen hängt.
   */
  async deleteSubprojectsOf(projectId: string): Promise<void> {
    for (const child of await this.projects.where('parentId').equals(projectId).filter(p => !p.deletedAt).toArray()) {
      await this.deleteProject(child.id)
    }
  }

  private async deleteOneProject(id: string): Promise<Omit<ProjectCascade, 'subprojectIds'>> {
    const project = await this.projects.get(id)
    await this.projects.update(id, { deletedAt: new Date() })

    // Collect this project's live sections — needed both to cascade them and to
    // catch their tasks (a section task may carry a null projectId).
    const sections = await this.sections
      .where('projectId')
      .equals(id)
      .filter(s => !s.deletedAt)
      .toArray()
    const sectionIds = sections.map(s => s.id)

    // Cascade: soft-delete all tasks belonging to this project — matched either by
    // projectId OR by living in one of its sections. Dedup so a task carrying both
    // is queued once.
    const [tasksByProject, tasksBySection] = await Promise.all([
      this.tasks.where('projectId').equals(id).filter(t => !t.deletedAt).toArray(),
      sectionIds.length
        ? this.tasks.where('sectionId').anyOf(sectionIds).filter(t => !t.deletedAt).toArray()
        : Promise.resolve([]),
    ])
    const seen = new Set<string>()
    const taskIds: string[] = []
    for (const task of [...tasksByProject, ...tasksBySection]) {
      if (seen.has(task.id)) continue
      seen.add(task.id)
      taskIds.push(task.id)
      await this.tasks.update(task.id, { deletedAt: new Date() })
      await this.queueSyncChange('delete', 'tasks', { localId: task.id, cloudId: task.cloudId })
    }

    // Cascade: die Kommentare dieser Aufgaben (#182) — Unteraufgaben tragen das
    // Projekt ihres Elternteils und stehen damit schon in `taskIds`.
    const commentIds = await this.deleteCommentsOfTasks(taskIds, new Date())
    const reminderIds = await this.deleteRemindersOfTasks(taskIds, new Date())

    // Cascade: soft-delete the sections themselves (was missing — left orphaned
    // cloud sections whose parent project was soft-deleted).
    for (const section of sections) {
      await this.sections.update(section.id, { deletedAt: new Date() })
      await this.queueSyncChange('delete', 'sections', { localId: section.id, cloudId: section.cloudId })
    }

    // Cascade: soft-delete this project's Projekt-Canvas notes (Notizen/Memos).
    const canvasNotes = await this.canvasNotes
      .where('projectId')
      .equals(id)
      .filter(n => !n.deletedAt)
      .toArray()
    for (const note of canvasNotes) {
      await this.canvasNotes.update(note.id, { deletedAt: new Date() })
      await this.queueSyncChange('delete', 'canvasNotes', { localId: note.id, cloudId: note.cloudId })
    }

    // Cascade: die Kommentare am Projekt selbst (#184).
    const projectCommentIds = await this.deleteCommentsOfProject(id)

    // Queue for cloud sync
    await this.queueSyncChange('delete', 'projects', { localId: id, cloudId: project?.cloudId })

    return { taskIds, sectionIds, canvasNoteIds: canvasNotes.map(n => n.id), commentIds, projectCommentIds, reminderIds }
  }

  /**
   * Der Rückweg zu {@link deleteProject} (Abnahme 21.09.2026, F-PR5).
   *
   * Zurück kommt das Projekt samt Unterprojekten und **genau das**, was die Kaskade gemeldet hat.
   * Eine Zeile, die die Cloud nie gesehen hat, braucht dabei ein `create` und
   * nicht das Update von nichts — wie in {@link restoreTasks}.
   */
  async restoreProject(id: string, cascade: ProjectCascade): Promise<void> {
    // Elternprojekt zuerst: Ein Kind geht erst hoch, wenn es sein Elternprojekt in der Cloud findet.
    for (const projectId of [id, ...(cascade.subprojectIds ?? [])]) {
      const project = await this.projects.get(projectId)
      if (!project) continue
      await this.projects.update(projectId, { deletedAt: null })
      await this.queueSyncChange(
        project.cloudId ? 'update' : 'create',
        'projects',
        { localId: projectId, cloudId: project.cloudId },
      )
    }

    for (const sectionId of cascade.sectionIds) {
      const section = await this.sections.get(sectionId)
      if (!section) continue
      await this.sections.update(sectionId, { deletedAt: null })
      await this.queueSyncChange(
        section.cloudId ? 'update' : 'create',
        'sections',
        { localId: sectionId, cloudId: section.cloudId },
      )
    }

    await this.restoreTasks(cascade.taskIds)
    await this.restoreComments(cascade.commentIds)
    await this.restoreProjectComments(cascade.projectCommentIds ?? [])
    await this.restoreReminders(cascade.reminderIds ?? [])

    for (const noteId of cascade.canvasNoteIds) {
      const note = await this.canvasNotes.get(noteId)
      if (!note) continue
      await this.canvasNotes.update(noteId, { deletedAt: null })
      await this.queueSyncChange(
        note.cloudId ? 'update' : 'create',
        'canvasNotes',
        { localId: noteId, cloudId: note.cloudId },
      )
    }
  }

  /**
   * **Ein Projekt zieht in einen anderen Lebensbereich** (#220) — mit allen seinen Aufgaben.
   * Aufgaben tragen ihren Lebensbereich selbst (`Task.lifeSpace`); blieben sie stehen, fehlten
   * sie im neuen Bereich in Demnächst, Ruhend und Geschafft.
   *
   * Gemeint sind dieselben Zeilen wie beim Löschen ({@link deleteProject}): nach `projectId` oder
   * in einem seiner Abschnitte — Unteraufgaben tragen beides von ihrer Aufgabe. **Gelöschte
   * zählen mit**, sonst käme eine später wiederhergestellte Aufgabe im alten Bereich zurück.
   * Das Projekt steht im Ziel oben, wie ein neu angelegtes.
   *
   * **Unterprojekte gehen mit** (#185), direkt unter ihm in derselben Reihenfolge. War das
   * Projekt selbst ein Unterprojekt, löst es sich vom Elternprojekt: Das bleibt, wo es ist.
   */
  async moveProjectToLifeSpace(projectId: string, lifeSpace: LifeSpace): Promise<ProjectLifeSpaceMove | null> {
    return this.transaction('rw', this.projects, this.sections, this.tasks, this.syncQueue, async () => {
      const project = await this.projects.get(projectId)
      if (!project || project.lifeSpace === lifeSpace) return null
      const subprojectIds = projectDescendantIds(await this.getProjectsByLifeSpace(project.lifeSpace), projectId)
      const move: ProjectLifeSpaceMove = {
        projectId,
        from: { lifeSpace: project.lifeSpace, order: project.order, parentId: project.parentId ?? null },
        subprojects: [],
        tasks: [],
      }
      const top = await this.getMinProjectOrder(lifeSpace) - 1 - subprojectIds.length
      await this.projects.update(projectId, { lifeSpace, order: top, parentId: null })
      await this.queueSyncChange('update', 'projects', { localId: projectId })
      for (const [i, id] of subprojectIds.entries()) {
        const sub = await this.projects.get(id)
        if (!sub) continue
        move.subprojects!.push({ id, lifeSpace: sub.lifeSpace, order: sub.order })
        await this.projects.update(id, { lifeSpace, order: top + 1 + i })
        await this.queueSyncChange('update', 'projects', { localId: id })
      }

      for (const id of [projectId, ...subprojectIds]) {
        for (const task of await this.tasksOfProject(id)) {
          if (task.lifeSpace === lifeSpace) continue
          move.tasks.push({ id: task.id, lifeSpace: task.lifeSpace })
          await this.tasks.update(task.id, { lifeSpace })
          await this.queueSyncChange('update', 'tasks', { localId: task.id })
        }
      }
      return move
    })
  }

  /** Der Rückweg zu {@link moveProjectToLifeSpace}: genau die gemerkten Werte, nicht „alles im Projekt“. */
  async restoreProjectLifeSpace(move: ProjectLifeSpaceMove): Promise<void> {
    await this.transaction('rw', this.projects, this.tasks, this.syncQueue, async () => {
      if (await this.projects.get(move.projectId)) {
        await this.projects.update(move.projectId, {
          lifeSpace: move.from.lifeSpace,
          order: move.from.order,
          ...(move.from.parentId !== undefined ? { parentId: move.from.parentId } : {}),
        })
        await this.queueSyncChange('update', 'projects', { localId: move.projectId })
      }
      for (const { id, lifeSpace, order } of move.subprojects ?? []) {
        if (!await this.projects.get(id)) continue
        await this.projects.update(id, { lifeSpace, order })
        await this.queueSyncChange('update', 'projects', { localId: id })
      }
      for (const { id, lifeSpace } of move.tasks) {
        if (!await this.tasks.get(id)) continue
        await this.tasks.update(id, { lifeSpace })
        await this.queueSyncChange('update', 'tasks', { localId: id })
      }
    })
  }

  /** Alle Aufgaben eines Projekts, gelöschte eingeschlossen — nach `projectId` oder über seine Abschnitte. */
  private async tasksOfProject(projectId: string): Promise<Task[]> {
    const sectionIds = (await this.sections.where('projectId').equals(projectId).toArray()).map(s => s.id)
    const [byProject, bySection] = await Promise.all([
      this.tasks.where('projectId').equals(projectId).toArray(),
      sectionIds.length ? this.tasks.where('sectionId').anyOf(sectionIds).toArray() : Promise.resolve([]),
    ])
    return uniqueById([...byProject, ...bySection])
  }

  async getAllProjects(): Promise<Project[]> {
    // System projects (quick-notes etc.) are hidden from general listings.
    return await this.projects
      .filter(p => !p.isSystem)
      .sortBy('name')
  }

  async getProjectsByLifeSpace(lifeSpace: string): Promise<Project[]> {
    const results = await this.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => !p.deletedAt && !p.isSystem)
      .sortBy('order')
    return results
  }

  async getProject(id: string): Promise<Project | null> {
    return await this.projects.get(id) || null
  }

  /** Look up the system project for a given lifeSpace + kind (e.g. "quick-notes").
   *  System projects are created on first access and hidden from the regular project list. */
  async getSystemProject(lifeSpace: string, systemKind: string): Promise<Project | null> {
    const results = await this.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => p.isSystem === true && p.systemKind === systemKind && !p.deletedAt)
      .toArray()
    return results[0] ?? null
  }

  private async getMinProjectOrder(lifeSpace: string): Promise<number> {
    const projects = await this.getProjectsByLifeSpace(lifeSpace)
    if (projects.length === 0) return 1
    return Math.min(...projects.map(p => p.order))
  }

  // Section operations
  async createSection(sectionData: CreateSection): Promise<Section> {
    const section: Section = {
      ...sectionData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      collapsed: sectionData.collapsed ?? false,
      order: sectionData.order ?? await this.getNextSectionOrder(sectionData.projectId),
      importedFrom: sectionData.importedFrom ?? null,
      description: sectionData.description ?? null,
      archivedAt: sectionData.archivedAt ?? null,
    }

    await this.sections.add(section)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'sections', { localId: section.id })

    return section
  }

  async updateSection(id: string, updates: Partial<UpdateSection>): Promise<Section | null> {
    await this.sections.update(id, updates)
    const updated = await this.sections.get(id) || null

    if (updated) {
      await this.queueSyncChange('update', 'sections', { localId: id })
    }

    return updated
  }

  async deleteSection(id: string): Promise<void> {
    await this.sections.update(id, { deletedAt: new Date() })

    // Unlink tasks from this section (set sectionId to null)
    const tasks = await this.tasks
      .where('sectionId')
      .equals(id)
      .filter(t => !t.deletedAt)
      .toArray()
    for (const task of tasks) {
      await this.tasks.update(task.id, { sectionId: null })
      await this.queueSyncChange('update', 'tasks', { localId: task.id })
    }

    await this.queueSyncChange('delete', 'sections', { localId: id })
  }

  /**
   * **Archivieren** (#193): setzt `archivedAt` und hakt die offenen Aufgaben des Abschnitts ab,
   * wie Todoist — sonst stünden sie weiter in Heute und Demnächst. Wiederkehrende bekommen
   * **keinen Nachfolger**: Die Serie endet, sonst stünde sofort wieder eine offene Aufgabe im
   * archivierten Abschnitt. Unteraufgaben tragen den Abschnitt ihrer Aufgabe und werden mit
   * abgehakt. Liefert die abgehakten IDs, damit Rückgängig genau sie wieder öffnet.
   */
  async archiveSection(id: string): Promise<{ completedTaskIds: string[] }> {
    return this.transaction('rw', this.sections, this.tasks, this.syncQueue, async () => {
      const now = new Date()
      const open = await this.tasks
        .where('sectionId')
        .equals(id)
        .filter(t => !t.deletedAt && t.status !== 'completed')
        .toArray()
      for (const task of open) {
        await this.tasks.update(task.id, { status: 'completed', completedAt: now })
        await this.queueSyncChange('update', 'tasks', { localId: task.id })
      }
      await this.sections.update(id, { archivedAt: now })
      await this.queueSyncChange('update', 'sections', { localId: id })
      return { completedTaskIds: open.map(t => t.id) }
    })
  }

  /**
   * **Wiederherstellen** (#193): `archivedAt` zurück auf `null`. Die Aufgaben bleiben erledigt —
   * außer denen in `reopen`: die IDs, die das Archivieren geliefert hat (Rückgängig), oder
   * `'closedByArchive'` („Mit Aufgaben wiederherstellen“, siehe {@link isClosedByArchive}).
   */
  async unarchiveSection(id: string, reopen: string[] | 'closedByArchive' = []): Promise<{ reopenedTaskIds: string[] }> {
    return this.transaction('rw', this.sections, this.tasks, this.syncQueue, async () => {
      const section = await this.sections.get(id)
      const candidates = reopen === 'closedByArchive'
        ? (section ? await this.tasks.where('sectionId').equals(id).filter(t => isClosedByArchive(t, section)).toArray() : [])
        : (await this.tasks.bulkGet(reopen)).filter((t): t is Task => !!t && !t.deletedAt && t.status === 'completed')
      await this.sections.update(id, { archivedAt: null })
      await this.queueSyncChange('update', 'sections', { localId: id })
      for (const task of candidates) {
        await this.tasks.update(task.id, { status: 'todo', completedAt: null })
        await this.queueSyncChange('update', 'tasks', { localId: task.id })
      }
      return { reopenedTaskIds: candidates.map(t => t.id) }
    })
  }

  async getSection(id: string): Promise<Section | null> {
    return await this.sections.get(id) || null
  }

  async getSectionsByProject(projectId: string): Promise<Section[]> {
    return await this.sections
      .where('projectId')
      .equals(projectId)
      .filter(s => !s.deletedAt)
      .sortBy('order')
  }

  /**
   * Get interleaved list of tasks and sections for a project, sorted by order.
   * Each item is tagged with `type: 'task' | 'section'` for rendering.
   */
  async getProjectItems(projectId: string): Promise<({ type: 'task' } & Task | { type: 'section' } & Section)[]> {
    const [tasks, sections] = await Promise.all([
      this.getTasksByProject(projectId),
      this.getSectionsByProject(projectId),
    ])

    const items: ({ type: 'task' } & Task | { type: 'section' } & Section)[] = [
      ...tasks.map(t => ({ type: 'task' as const, ...t })),
      ...sections.map(s => ({ type: 'section' as const, ...s })),
    ]

    items.sort((a, b) => a.order - b.order)
    return items
  }

  private async getNextSectionOrder(projectId: string): Promise<number> {
    const items = await this.getProjectItems(projectId)
    if (items.length === 0) return 0
    return Math.max(...items.map(i => i.order)) + 1
  }

  // Goal operations
  // A Goal is a statement of direction for one life space — NOT a task.
  // See CONTEXT.md. Separate entity, separate table.
  async createGoal(goalData: CreateGoal & { id?: string }): Promise<Goal> {
    const goal: Goal = {
      ...goalData,
      id: goalData.id ?? crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      description: goalData.description ?? null,
      userId: goalData.userId ?? null,
      cloudId: null,
      deletedAt: null,
    }

    await this.goals.add(goal)

    // Queue for cloud sync
    await this.queueSyncChange('create', 'goals', { localId: goal.id })

    return goal
  }

  async updateGoal(id: string, updates: Partial<UpdateGoal>): Promise<Goal | null> {
    await this.goals.update(id, updates)
    const updated = await this.goals.get(id) || null

    // Queue for cloud sync
    if (updated) {
      await this.queueSyncChange('update', 'goals', { localId: id })
    }

    return updated
  }

  async deleteGoal(id: string): Promise<void> {
    const goal = await this.goals.get(id)
    await this.goals.update(id, { deletedAt: new Date() })

    // Queue for cloud sync
    await this.queueSyncChange('delete', 'goals', { localId: id, cloudId: goal?.cloudId })
  }

  async getGoal(id: string): Promise<Goal | null> {
    return await this.goals.get(id) || null
  }

  /** Goals for a life space, newest-first (Grilling decision #2 — no manual ordering). */
  async getGoalsByLifeSpace(lifeSpace: LifeSpace): Promise<Goal[]> {
    const goals = await this.goals
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(g => !g.deletedAt)
      .toArray()
    return goals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  }

  // ============================================================
  // Habit operations (Gewohnheiten)
  // A Habit is a recurring intention with a per-day completion log.
  // See .claude/tasks/habits-feature.md.
  // ============================================================
  async createHabit(habitData: CreateHabit & { id?: string }): Promise<Habit> {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const habit: Habit = {
      ...habitData,
      id: habitData.id ?? crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
      notes: habitData.notes ?? null,
      startDate: habitData.startDate ?? today,
      rewardId: habitData.rewardId ?? null,
      targetMinutes: habitData.targetMinutes ?? null,
      pausedAt: null,
      sortOrder: habitData.sortOrder ?? await this.getNextHabitOrder(habitData.lifeSpace),
      userId: habitData.userId ?? null,
      cloudId: null,
      deletedAt: null,
    }

    await this.habits.add(habit)
    await this.queueSyncChange('create', 'habits', { localId: habit.id })
    return habit
  }

  async updateHabit(id: string, updates: Partial<UpdateHabit>): Promise<Habit | null> {
    await this.habits.update(id, updates)
    const updated = await this.habits.get(id) || null
    if (updated) {
      await this.queueSyncChange('update', 'habits', { localId: id })
    }
    return updated
  }

  /** Pause/freeze a habit (archived): no longer due, streak frozen, resumable. */
  async pauseHabit(id: string): Promise<Habit | null> {
    return this.updateHabit(id, { pausedAt: new Date() })
  }

  async resumeHabit(id: string): Promise<Habit | null> {
    return this.updateHabit(id, { pausedAt: null })
  }

  async deleteHabit(id: string): Promise<void> {
    const habit = await this.habits.get(id)
    await this.habits.update(id, { deletedAt: new Date() })
    // Logs are intentionally kept (restore-friendly) — see D13.
    await this.queueSyncChange('delete', 'habits', { localId: id, cloudId: habit?.cloudId })
  }

  async getHabit(id: string): Promise<Habit | null> {
    return await this.habits.get(id) || null
  }

  /** Active (non-deleted) habits for a life space, by sortOrder.
   *  `includePaused=false` hides paused/archived habits. */
  async getHabitsByLifeSpace(lifeSpace: LifeSpace, includePaused = true): Promise<Habit[]> {
    const habits = await this.habits
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(h => !h.deletedAt && (includePaused || !h.pausedAt))
      .toArray()
    return habits.sort((a, b) => a.sortOrder - b.sortOrder)
  }

  private async getNextHabitOrder(lifeSpace: LifeSpace): Promise<number> {
    const habits = await this.getHabitsByLifeSpace(lifeSpace)
    if (habits.length === 0) return 0
    return Math.max(...habits.map(h => h.sortOrder)) + 1
  }

  // ---- Habit logs (per-day completion) ----

  /** Upsert the completion flag for (habitId, date). Stable row, flips `completed`. */
  async setHabitLog(habitId: string, date: string, completed: boolean): Promise<HabitLog> {
    const existing = await this.habitLogs.where('[habitId+date]').equals([habitId, date]).first()
    if (existing) {
      await this.habitLogs.update(existing.id, { completed, deletedAt: null })
      await this.queueSyncChange('update', 'habitLogs', { localId: existing.id })
      return (await this.habitLogs.get(existing.id))!
    }
    const now = new Date()
    const log: HabitLog = {
      id: crypto.randomUUID(),
      habitId,
      date,
      completed,
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
      userId: null,
      cloudId: null,
      deletedAt: null,
    }
    await this.habitLogs.add(log)
    await this.queueSyncChange('create', 'habitLogs', { localId: log.id })
    return log
  }

  /** All (non-deleted) logs for one habit, oldest-first — for streak computation. */
  async getHabitLogs(habitId: string): Promise<HabitLog[]> {
    const logs = await this.habitLogs
      .where('habitId')
      .equals(habitId)
      .filter(l => !l.deletedAt)
      .toArray()
    return logs.sort((a, b) => a.date.localeCompare(b.date))
  }

  /** Logs across all habits for a single day — for the day's checklist state. */
  async getHabitLogsByDate(date: string): Promise<HabitLog[]> {
    return await this.habitLogs
      .where('date')
      .equals(date)
      .filter(l => !l.deletedAt)
      .toArray()
  }

  // ---- Unteraufgaben: Aufgaben mit Elternteil (T14 #179) ----
  //
  // Bis Etappe 2 lebten sie in der eigenen Tabelle `subtasks`; die bleibt nur als
  // Quelle der Umwandlung stehen (`convertLegacySubtasks`). Nach außen behalten die
  // Methoden ihre Form (`Subtask`), damit Checkliste, Fokus und Import unverändert
  // weiterlaufen.

  /** Eine Kind-Aufgabe in der Form der Checkliste. */
  private asSubtask(task: Task): Subtask {
    return {
      id: task.id,
      taskId: task.parentTaskId ?? '',
      title: task.title,
      done: task.status === 'completed',
      sortOrder: task.order,
      userId: task.userId,
      cloudId: task.cloudId,
      deletedAt: task.deletedAt,
      importedFrom: task.importedFrom ?? null,
      attention: task.attention,
      startDate: task.startDate,
      startTime: task.startTime,
      startPeriod: task.startPeriod,
      dueDate: task.dueDate,
      dueTime: task.dueTime,
      duePeriod: task.duePeriod,
      estimatedDuration: task.estimatedDuration,
      durationMode: task.durationMode,
      tags: task.tags,
      pausedAt: task.pausedAt,
      rewardId: task.rewardId,
      recurrence: task.recurrence,
      createdAt: task.createdAt,
      updatedAt: task.updatedAt,
      lastModified: task.lastModified,
      synced: task.synced,
      syncedAt: task.syncedAt,
    }
  }

  /** Die lebenden Kinder einer Aufgabe, in Checklisten-Reihenfolge. */
  async getChildTasks(parentId: string): Promise<Task[]> {
    const rows = await this.tasks
      .where('parentTaskId')
      .equals(parentId)
      .filter(t => !t.deletedAt)
      .toArray()
    return rows.map(t => this.normalizeTask(t)).sort((a, b) => a.order - b.order)
  }

  /**
   * Legt eine Unteraufgabe an. **Ort und Lebensbereich kommen vom Elternteil** —
   * sie steht in keiner Liste für sich, aber Demnächst und der Sync lesen sie.
   */
  private async addChild(
    parentId: string,
    fields: SubtaskDraft & { order: number },
  ): Promise<Task> {
    const parent = await this.getTask(parentId)
    const estimatedDuration = fields.estimatedDuration ?? null
    return this.createTask({
      title: fields.title,
      description: fields.description ?? null,
      status: fields.done ? 'completed' : 'todo',
      completedAt: fields.done ? (fields.completedAt ?? new Date()) : null,
      attention: fields.attention ?? null,
      category: 'other',
      lifeSpace: parent?.lifeSpace ?? 'personal',
      projectId: parent?.projectId ?? null,
      sectionId: parent?.sectionId ?? null,
      startDate: fields.startDate ?? null,
      startTime: fields.startTime ?? null,
      dueDate: fields.dueDate ?? null,
      estimatedDuration,
      durationMode: estimatedDuration !== null ? (fields.durationMode ?? 'duration') : null,
      actualDuration: null,
      tags: fields.tags ?? [],
      recurrence: fields.recurrence ?? null,
      parentTaskId: parentId,
      pausedAt: null,
      order: fields.order,
      importedFrom: fields.importedFrom ?? null,
      completedCount: fields.completedCount ?? null,
      postponedCount: fields.postponedCount ?? null,
      subtasks: fields.subtasks,
      comments: fields.comments,
      reminders: fields.reminders,
    })
  }

  /** Appends a subtask to the end of its task's checklist. */
  async createSubtask(taskId: string, title: string): Promise<Subtask> {
    await this.assertRoomBelow(taskId)
    const existing = await this.getChildTasks(taskId)
    const order = existing.length === 0 ? 0 : Math.max(...existing.map(s => s.order)) + 1
    return this.asSubtask(await this.addChild(taskId, { title, done: false, order }))
  }

  /** Unter Ebene {@link MAX_SUBTASK_DEPTH} hängt nichts mehr (#181) — die Oberfläche bietet es gar nicht an. */
  private async assertRoomBelow(parentId: string): Promise<void> {
    if ((await this.getAncestorTasks(parentId)).length >= MAX_SUBTASK_DEPTH) {
      throw new Error(`Subtask depth: nothing below level ${MAX_SUBTASK_DEPTH}`)
    }
  }

  /**
   * Legt eine Zeile direkt hinter `afterId` an, ohne eine andere anzufassen:
   * Sie bekommt den Mittelwert zwischen ihrem Vorgänger und dessen Nachfolger.
   *
   * Nur wenn dort kein Platz mehr ist (zwei Nachbarn auf demselben Wert), wird
   * die Liste einmal neu durchnummeriert — siehe {@link orderBetween}.
   */
  async insertSubtaskAfter(taskId: string, afterId: string, title: string): Promise<Subtask> {
    await this.assertRoomBelow(taskId)
    const existing = await this.orderedChildrenForWriting(taskId)
    const index = existing.findIndex(s => s.id === afterId)
    // Die genannte Zeile gibt es nicht (mehr) — dann ist „dahinter" das Ende.
    if (index === -1) return this.createSubtask(taskId, title)

    let order = orderBetween(existing[index]!.order, existing[index + 1]?.order)
    if (order === null) {
      await this.renumberChildren(existing)
      order = orderBetween(index, index + 1)!
    }
    return this.asSubtask(await this.addChild(taskId, { title, done: false, order }))
  }

  /**
   * Schiebt eine Zeile hinter `afterId`; `null` heißt an den Anfang. Geschrieben
   * wird **nur die bewegte Zeile** (außer im seltenen Fall, dass neu
   * durchnummeriert werden muss).
   */
  async moveSubtask(id: string, afterId: string | null): Promise<void> {
    const child = await this.tasks.get(id)
    if (!child?.parentTaskId) return

    const existing = (await this.orderedChildrenForWriting(child.parentTaskId)).filter(s => s.id !== id)
    const index = afterId === null ? -1 : existing.findIndex(s => s.id === afterId)
    if (afterId !== null && index === -1) return

    let order = orderBetween(existing[index]?.order, existing[index + 1]?.order)
    if (order === null) {
      await this.renumberChildren(existing)
      order = orderBetween(index, index + 1)!
    }
    await this.updateTask(id, { order })
  }

  /**
   * Die Liste einer Aufgabe, bereit zum Rechnen.
   *
   * Stehen zwei Zeilen auf demselben Wert, hat die Liste keine verlässliche
   * Reihenfolge — dann wird sie **vorher** neu durchnummeriert. Ohne das träfe
   * schon die Suche nach der Nachbarzeile auf gut Glück, und ein Einschub
   * landete mal hier, mal dort.
   */
  private async orderedChildrenForWriting(parentId: string): Promise<Task[]> {
    const existing = await this.getChildTasks(parentId)
    if (hasUniqueOrder(existing.map(s => s.order))) return existing
    await this.renumberChildren(existing)
    return existing.map((s, i) => ({ ...s, order: i }))
  }

  /** Vergibt 0, 1, 2 … neu — der Notausgang, wenn zwischen zwei Zeilen kein Platz mehr ist. */
  private async renumberChildren(ordered: Task[]): Promise<void> {
    for (const [i, child] of ordered.entries()) {
      if (child.order === i) continue
      await this.updateTask(child.id, { order: i })
    }
  }

  /**
   * Holt eine gelöschte Zeile zurück — der Rückweg des Undo-Toasts. Sie kommt mit ihrem alten
   * `order` und damit an ihren Platz; mit der `cascade` aus {@link deleteSubtask} ihr Ast und ihre
   * Fokuszeit dazu (#181).
   */
  async restoreSubtask(id: string, cascade?: TaskCascade): Promise<void> {
    await this.restoreTaskSubtasks([id, ...(cascade?.subtaskIds ?? [])])
    if (!cascade) return
    await this.relinkCanvasNotes(cascade.unlinkedNoteIds, id)
    await this.restoreTaskTimeEntries(cascade.timeEntryIds ?? [])
    await this.restoreComments(cascade.commentIds)
    await this.restoreReminders(cascade.reminderIds ?? [])
  }

  /** Die Checkliste, in ihrer Reihenfolge: ohne erledigte Runden, deren Nachfolger schon dasteht (#205). */
  async getSubtasks(taskId: string): Promise<Subtask[]> {
    return withoutSucceededRounds(await this.getChildTasks(taskId), await this.getDescendantTasks(taskId)).map(t => this.asSubtask(t))
  }

  /** Die Checkliste als Baum (#181), in Vorordnung; ohne erledigte Runden samt deren Ast. */
  async getSubtaskTree(taskId: string): Promise<SubtaskTreeRow[]> {
    const family = await this.getDescendantTasks(taskId)
    return treeEntriesOf(family, taskId, siblings => withoutSucceededRounds(siblings, family))
      .map(({ task, depth, progress }) => ({ ...this.asSubtask(task), depth, progress }))
  }

  /**
   * **Die Checklisten vieler Aufgaben auf einmal** (#303) — wie `getSubtaskTree` je Aufgabe, aber
   * Ebene für Ebene EINE Abfrage für alle. Eine Liste mit 150 Zeilen hält so ein Abo statt 150.
   * Steht eine genannte Aufgabe unter einer anderen genannten, bekommt jede ihren eigenen Ast.
   */
  async getSubtaskTrees(taskIds: readonly string[]): Promise<Map<string, SubtaskTreeRow[]>> {
    const families = new Map<string, Task[]>()
    const seen = new Map<string, Set<string>>()
    // Eltern-Kennung → die Aufgaben, unter deren Baum sie steht.
    let frontier = new Map<string, string[]>()
    for (const id of taskIds) {
      if (families.has(id)) continue
      families.set(id, [])
      seen.set(id, new Set([id]))
      frontier.set(id, [id])
    }
    while (frontier.size > 0) {
      const rows = await this.tasks.where('parentTaskId').anyOf([...frontier.keys()]).filter(t => !t.deletedAt).toArray()
      const next = new Map<string, string[]>()
      for (const row of rows) {
        let normalized: Task | undefined
        for (const root of frontier.get(row.parentTaskId!) ?? []) {
          const visited = seen.get(root)!
          if (visited.has(row.id)) continue
          visited.add(row.id)
          normalized ??= this.normalizeTask(row)
          families.get(root)!.push(normalized)
          next.set(row.id, [...(next.get(row.id) ?? []), root])
        }
      }
      frontier = next
    }
    const trees = new Map<string, SubtaskTreeRow[]>()
    for (const [id, family] of families) {
      trees.set(id, treeEntriesOf(family, id, siblings => withoutSucceededRounds(siblings, family))
        .map(({ task, depth, progress }) => ({ ...this.asSubtask(task), depth, progress })))
    }
    return trees
  }

  /** Eine Unteraufgabe, gelöschte eingeschlossen; `undefined`, wenn es keine ist. */
  async getSubtask(id: string): Promise<Subtask | undefined> {
    const task = await this.tasks.get(id)
    return task?.parentTaskId ? this.asSubtask(this.normalizeTask(task)) : undefined
  }

  async updateSubtask(id: string, updates: UpdateSubtask): Promise<Subtask | null> {
    const changes: Partial<UpdateTask> = {}
    if (updates.title !== undefined) changes.title = updates.title
    if (updates.sortOrder !== undefined) changes.order = updates.sortOrder
    if (updates.deletedAt !== undefined) changes.deletedAt = updates.deletedAt
    if (updates.done !== undefined) {
      changes.status = updates.done ? 'completed' : 'todo'
      changes.completedAt = updates.done ? new Date() : null
    }
    const updated = await this.updateTask(id, changes)
    return updated ? this.asSubtask(updated) : null
  }

  /** Löscht die Zeile samt Ast (#181, Grill 10); die Kaskade ist für {@link restoreSubtask}. */
  async deleteSubtask(id: string): Promise<TaskCascade> {
    return this.deleteTask(id)
  }

  /**
   * Undo counterpart to the cascade in {@link deleteTask}: restores exactly the
   * subtasks that call took, leaving anything the user had deleted by hand
   * beforehand where it is.
   */
  async restoreTaskSubtasks(subtaskIds: string[]): Promise<void> {
    for (const id of subtaskIds) {
      const child = await this.tasks.get(id)
      if (!child) continue
      await this.tasks.update(id, { deletedAt: null })
      // A row the cloud has never seen needs a create, not an update of nothing.
      await this.queueSyncChange(
        child.cloudId ? 'update' : 'create',
        'tasks',
        { localId: id, cloudId: child.cloudId },
      )
    }
  }

  /**
   * Copies a task's checklist onto another task, 1:1 including tick state,
   * order and every level below (#181), under fresh ids. Used when a task is duplicated — the duplicate of a
   * checklist task without its checklist is almost never what was meant.
   *
   * `resetDone`: die Liste kommt mit, erledigt ist noch nichts — für den **Nachfolger**
   * einer Wiederholung (ADR 0021). `shiftDays`: Wann und Deadline der Kopien wandern
   * im selben Abstand wie die Runde (Grill 6). Duplizieren lässt beides, wie es ist.
   * `copied` sammelt Original → Kopie über alle Ebenen — der Nachfolger hängt damit die
   * Kommentare um (#182). Kommentare selbst kopiert das nie: Eine Kopie ist eine neue Sache.
   */
  async copySubtasks(
    fromTaskId: string,
    toTaskId: string,
    options: { resetDone?: boolean; shiftDays?: number } = {},
    family?: Task[],
    copied?: Map<string, string>,
  ): Promise<Subtask[]> {
    // Nur die Checkliste: Erledigte Runden gehören zu ihrer Aufgabe und stehen schon in Geschafft —
    // ihr Nachfolger kann auf jeder Ebene des kopierten Baums stehen (#181).
    const rows = family ?? await this.getDescendantTasks(fromTaskId)
    const [source, target] = [withoutSucceededRounds(await this.getChildTasks(fromTaskId), rows), await this.getTask(toTaskId)]
    const shift = (date: Date | null) => (date && options.shiftDays ? addDays(date, options.shiftDays) : date)
    const copies: Subtask[] = []
    for (const child of source) {
      const done = options.resetDone ? false : child.status === 'completed'
      const { id: _id, createdAt: _c, updatedAt: _u, lastModified: _l, synced: _s, syncedAt: _sa, userId: _uid, cloudId: _cid, adoptCloudId: _adopt, ...fields } = child
      const copy = await this.createTask({
        ...fields,
        status: done ? 'completed' : 'todo',
        completedAt: done ? child.completedAt : null,
        startDate: shift(child.startDate),
        dueDate: shift(child.dueDate),
        lifeSpace: target?.lifeSpace ?? child.lifeSpace,
        projectId: target?.projectId ?? null,
        sectionId: target?.sectionId ?? null,
        parentTaskId: toTaskId,
        // Kopien sind keine Todoist-Zeilen — und keine Favoriten (#210).
        importedFrom: null,
        favoriteOrder: null,
        dayOrder: null,
        // Ihre Erinnerungen holt der Aufrufer über `copied` (Kopie: `copyReminders`, Nachfolger: `moveReminders`).
        reminders: [],
      })
      copies.push(this.asSubtask(copy))
      copied?.set(child.id, copy.id)
      // Der Ast darunter kommt in seiner Form mit (#181).
      await this.copySubtasks(child.id, copy.id, options, rows, copied)
    }
    return copies
  }

  // ---- Kommentare (#182) ----

  /** Die lebenden Kommentare einer Aufgabe, der älteste zuerst. */
  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    const rows = await this.taskComments.where('taskId').equals(taskId).filter(c => !c.deletedAt).toArray()
    return rows.sort((a, b) => a.postedAt.getTime() - b.postedAt.getTime())
  }

  /** Wie viele lebende Kommentare eine Aufgabe hat — für die Marke an der Zeile. */
  async countTaskComments(taskId: string): Promise<number> {
    return this.taskComments.where('taskId').equals(taskId).filter(c => !c.deletedAt).count()
  }

  /** Wie `countTaskComments`, für viele Aufgaben in EINER Abfrage (#303); jede genannte steht drin, auch mit `0`. */
  async countTaskCommentsByTask(taskIds: readonly string[]): Promise<Map<string, number>> {
    const counts = new Map<string, number>(taskIds.map(id => [id, 0]))
    if (taskIds.length === 0) return counts
    const rows = await this.taskComments.where('taskId').anyOf([...taskIds]).filter(c => !c.deletedAt).toArray()
    for (const row of rows) counts.set(row.taskId, (counts.get(row.taskId) ?? 0) + 1)
    return counts
  }

  /** Schreibt einen Kommentar an eine Aufgabe; ohne `postedAt` gilt jetzt. */
  async createComment(taskId: string, draft: CommentDraft): Promise<TaskComment> {
    const now = new Date()
    const comment: TaskComment = {
      id: crypto.randomUUID(),
      taskId,
      content: draft.content,
      postedAt: draft.postedAt ?? now,
      importedFrom: draft.importedFrom ?? null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
    }
    await this.taskComments.add(comment)
    await this.queueSyncChange('create', 'taskComments', { localId: comment.id })
    return comment
  }

  /** Ändert den Text. `postedAt` bleibt — der Kommentar behält seinen Platz, ohne Vermerk. */
  async updateComment(id: string, content: string): Promise<void> {
    const updated = await this.taskComments.update(id, { content })
    if (updated) await this.queueSyncChange('update', 'taskComments', { localId: id })
  }

  /** Weich löschen; {@link restoreComments} ist der Rückweg. */
  async deleteComment(id: string): Promise<void> {
    const comment = await this.taskComments.get(id)
    if (!comment || comment.deletedAt) return
    await this.taskComments.update(id, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'taskComments', { localId: id, cloudId: comment.cloudId })
  }

  /**
   * Holt genau diese Kommentare zurück — nach {@link deleteComment} oder als Teil einer
   * Kaskade ({@link TaskCascade.commentIds}, {@link ProjectCascade.commentIds}).
   */
  async restoreComments(ids: readonly string[]): Promise<void> {
    for (const id of ids) {
      const comment = await this.taskComments.get(id)
      if (!comment) continue
      await this.taskComments.update(id, { deletedAt: null })
      // Eine Zeile, die die Cloud nie gesehen hat, braucht ein `create`.
      await this.queueSyncChange(
        comment.cloudId ? 'update' : 'create',
        'taskComments',
        { localId: id, cloudId: comment.cloudId },
      )
    }
  }

  /**
   * **Hängt Kommentare um** — je Eintrag alle lebenden von der einen Aufgabe an die andere.
   * Beim Abhaken einer wiederkehrenden Aufgabe gehen sie an den Nachfolger (ADR 0021,
   * Nachtrag), beim Haken zurück wieder an die alte Runde.
   */
  async moveComments(moves: ReadonlyMap<string, string>): Promise<void> {
    for (const [fromTaskId, toTaskId] of moves) {
      if (fromTaskId === toTaskId) continue
      const comments = await this.taskComments.where('taskId').equals(fromTaskId).filter(c => !c.deletedAt).toArray()
      for (const comment of comments) {
        await this.taskComments.update(comment.id, { taskId: toTaskId })
        await this.queueSyncChange('update', 'taskComments', { localId: comment.id })
      }
    }
  }

  /**
   * Die Kaskade: löscht die lebenden Kommentare dieser Aufgaben weich und reiht je einen
   * Eintrag ein. Gibt die Kennungen zurück, damit das Rückgängig genau sie zurückholt —
   * nicht einen, den der Nutzer vorher selbst gelöscht hat.
   */
  async deleteCommentsOfTasks(taskIds: readonly string[], deletedAt: Date = new Date()): Promise<string[]> {
    if (taskIds.length === 0) return []
    const comments = await this.taskComments.where('taskId').anyOf([...taskIds]).filter(c => !c.deletedAt).toArray()
    for (const comment of comments) {
      await this.taskComments.update(comment.id, { deletedAt })
      await this.queueSyncChange('delete', 'taskComments', { localId: comment.id, cloudId: comment.cloudId })
    }
    return comments.map(c => c.id)
  }

  // ---- Erinnerungen (Plan task-reminders) — dieselben Wege wie die Kommentare ----

  /** Die lebenden Erinnerungen einer Aufgabe, in der Reihenfolge ihres Anlegens. */
  async getTaskReminders(taskId: string): Promise<TaskReminder[]> {
    const rows = await this.taskReminders.where('taskId').equals(taskId).filter(r => !r.deletedAt).toArray()
    return rows.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
  }

  /** Wie `getTaskReminders`, für viele Aufgaben in EINER Abfrage (#303); jede genannte steht drin, auch leer. */
  async getRemindersByTask(taskIds: readonly string[]): Promise<Map<string, TaskReminder[]>> {
    const byTask = new Map<string, TaskReminder[]>(taskIds.map(id => [id, []]))
    if (taskIds.length === 0) return byTask
    const rows = await this.taskReminders.where('taskId').anyOf([...taskIds]).filter(r => !r.deletedAt).toArray()
    rows.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
    for (const row of rows) byTask.get(row.taskId)?.push(row)
    return byTask
  }

  /** Alle lebenden Erinnerungen — für den Zeitplan. */
  async getLiveReminders(): Promise<TaskReminder[]> {
    return this.taskReminders.filter(r => !r.deletedAt).toArray()
  }

  async createReminder(taskId: string, draft: ReminderDraft): Promise<TaskReminder> {
    const reminder = reminderFromDraft(taskId, draft, new Date())
    await this.taskReminders.add(reminder)
    await this.queueSyncChange('create', 'taskReminders', { localId: reminder.id })
    return reminder
  }

  async updateReminder(id: string, changes: ReminderChanges): Promise<void> {
    const updated = await this.taskReminders.update(id, changes)
    // `taskUnchanged`: Der Push schickt die Aufgabe nicht mit — ein Gerät ohne den neuesten Stand
    // hängte sie sonst an eine Runde zurück, von der ein anderes sie längst weggehängt hat.
    if (updated) await this.queueSyncChange('update', 'taskReminders', { localId: id, taskUnchanged: true })
  }

  async deleteReminder(id: string): Promise<void> {
    const reminder = await this.taskReminders.get(id)
    if (!reminder || reminder.deletedAt) return
    await this.taskReminders.update(id, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'taskReminders', { localId: id, cloudId: reminder.cloudId })
  }

  /** Holt genau diese Erinnerungen zurück — nach {@link deleteReminder} oder als Teil einer Kaskade. */
  async restoreReminders(ids: readonly string[]): Promise<void> {
    for (const id of ids) {
      const reminder = await this.taskReminders.get(id)
      if (!reminder) continue
      await this.taskReminders.update(id, { deletedAt: null })
      // Eine Zeile, die die Cloud nie gesehen hat, braucht ein `create`.
      await this.queueSyncChange(reminder.cloudId ? 'update' : 'create', 'taskReminders', { localId: id, cloudId: reminder.cloudId })
    }
  }

  /**
   * **Hängt Erinnerungen um** — beim Abhaken einer wiederkehrenden Aufgabe an den Nachfolger
   * (ADR 0021), beim Haken zurück wieder an die alte Runde. Relative gelten so jede Runde, eine
   * einmalige feste liegt danach in der Vergangenheit und bleibt still (wie bei Todoist).
   */
  async moveReminders(moves: ReadonlyMap<string, string>): Promise<void> {
    for (const [fromTaskId, toTaskId] of moves) {
      if (fromTaskId === toTaskId) continue
      const reminders = await this.taskReminders.where('taskId').equals(fromTaskId).filter(r => !r.deletedAt).toArray()
      for (const reminder of reminders) {
        await this.taskReminders.update(reminder.id, { taskId: toTaskId, snoozedUntil: null })
        await this.queueSyncChange('update', 'taskReminders', { localId: reminder.id })
      }
    }
  }

  /**
   * **Die Erinnerungen einer Kopie** (Duplizieren): je Eintrag die relativen des Originals — sie
   * passen an der Kopie, samt Standard-Erinnerung. Feste gehören zu ihrem Zeitpunkt, nicht zur Kopie.
   */
  async copyReminders(copies: ReadonlyMap<string, string>): Promise<void> {
    for (const [fromTaskId, toTaskId] of copies) {
      for (const reminder of await this.getTaskReminders(fromTaskId)) {
        if (reminder.kind !== 'relative') continue
        await this.createReminder(toTaskId, { kind: 'relative', anchor: reminder.anchor ?? 'start', offsetMinutes: reminder.offsetMinutes })
      }
    }
  }

  /** Die Kaskade: löscht die lebenden Erinnerungen dieser Aufgaben weich; gibt ihre Kennungen fürs Rückgängig zurück. */
  async deleteRemindersOfTasks(taskIds: readonly string[], deletedAt: Date = new Date()): Promise<string[]> {
    if (taskIds.length === 0) return []
    const reminders = await this.taskReminders.where('taskId').anyOf([...taskIds]).filter(r => !r.deletedAt).toArray()
    for (const reminder of reminders) {
      await this.taskReminders.update(reminder.id, { deletedAt })
      await this.queueSyncChange('delete', 'taskReminders', { localId: reminder.id, cloudId: reminder.cloudId })
    }
    return reminders.map(r => r.id)
  }

  // ---- Kommentare an Projekten (#184) — dieselben Wege wie oben, am Projekt ----

  /** Die lebenden Kommentare eines Projekts, der älteste zuerst. */
  async getProjectComments(projectId: string): Promise<ProjectComment[]> {
    const rows = await this.projectComments.where('projectId').equals(projectId).filter(c => !c.deletedAt).toArray()
    return rows.sort((a, b) => a.postedAt.getTime() - b.postedAt.getTime())
  }

  /** Wie viele lebende Kommentare ein Projekt hat — für die Zahl am Reiter. */
  async countProjectComments(projectId: string): Promise<number> {
    return this.projectComments.where('projectId').equals(projectId).filter(c => !c.deletedAt).count()
  }

  /** Schreibt einen Kommentar an ein Projekt; ohne `postedAt` gilt jetzt. */
  async createProjectComment(projectId: string, draft: CommentDraft): Promise<ProjectComment> {
    const now = new Date()
    const comment: ProjectComment = {
      id: crypto.randomUUID(),
      projectId,
      content: draft.content,
      postedAt: draft.postedAt ?? now,
      importedFrom: draft.importedFrom ?? null,
      userId: null,
      cloudId: null,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
    }
    await this.projectComments.add(comment)
    await this.queueSyncChange('create', 'projectComments', { localId: comment.id })
    return comment
  }

  /** Ändert den Text; `postedAt` bleibt. */
  async updateProjectComment(id: string, content: string): Promise<void> {
    const updated = await this.projectComments.update(id, { content })
    if (updated) await this.queueSyncChange('update', 'projectComments', { localId: id })
  }

  /** Weich löschen; {@link restoreProjectComments} ist der Rückweg. */
  async deleteProjectComment(id: string): Promise<void> {
    const comment = await this.projectComments.get(id)
    if (!comment || comment.deletedAt) return
    await this.projectComments.update(id, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'projectComments', { localId: id, cloudId: comment.cloudId })
  }

  /** Holt genau diese Kommentare zurück — nach {@link deleteProjectComment} oder aus einer Projekt-Kaskade. */
  async restoreProjectComments(ids: readonly string[]): Promise<void> {
    for (const id of ids) {
      const comment = await this.projectComments.get(id)
      if (!comment) continue
      await this.projectComments.update(id, { deletedAt: null })
      await this.queueSyncChange(
        comment.cloudId ? 'update' : 'create',
        'projectComments',
        { localId: id, cloudId: comment.cloudId },
      )
    }
  }

  /** Die Kaskade beim Löschen eines Projekts — auch, wenn die Löschung per Pull ankommt. */
  async deleteCommentsOfProject(projectId: string, deletedAt: Date = new Date()): Promise<string[]> {
    const comments = await this.projectComments.where('projectId').equals(projectId).filter(c => !c.deletedAt).toArray()
    for (const comment of comments) {
      await this.projectComments.update(comment.id, { deletedAt })
      await this.queueSyncChange('delete', 'projectComments', { localId: comment.id, cloudId: comment.cloudId })
    }
    return comments.map(c => c.id)
  }

  // ---- Projekt-Canvas notes (Notizen & Memos) ----

  async createCanvasNote(noteData: CreateCanvasNote & { id?: string }): Promise<CanvasNote> {
    const note: CanvasNote = {
      ...noteData,
      id: noteData.id ?? crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
      syncedAt: null,
      title: noteData.title ?? null,
      width: noteData.width ?? 0,
      height: noteData.height ?? 0,
      // **Gerade geboren.** Anders als Schatten und Klebestreifen wird die
      // Neigung nicht gewürfelt: Schräg wird nur, was die Hand schräg legt
      // (CONTEXT.md, „Neigung"). Eine Kopie bringt ihre eigene mit.
      rotation: noteData.rotation ?? 0,
      color: noteData.color ?? null,
      icon: noteData.icon ?? null,
      showAuthor: noteData.showAuthor ?? false,
      font: noteData.font ?? null,
      fontSize: noteData.fontSize ?? null,
      textAlign: noteData.textAlign ?? null,
      // Nach Alter — ein neues Element liegt obenauf (CONTEXT.md, „Vorn und hinten").
      zOrder: null,
      areaId: noteData.areaId ?? null,
      tape: noteData.tape ?? null,
      taskId: null,
      convertedAt: null,
      convertedContent: null,
      userId: noteData.userId ?? null,
      cloudId: null,
      deletedAt: null,
    }

    await this.canvasNotes.add(note)
    await this.queueSyncChange('create', 'canvasNotes', { localId: note.id })

    return note
  }

  /**
   * Live (non-deleted) Notizen/Memos of one project, in no particular
   * order — position on the canvas comes from x/y, not list order.
   */
  async getCanvasNotes(projectId: string): Promise<CanvasNote[]> {
    return await this.canvasNotes
      .where('projectId')
      .equals(projectId)
      .filter(n => !n.deletedAt)
      .toArray()
  }

  async updateCanvasNote(id: string, updates: UpdateCanvasNote): Promise<CanvasNote | null> {
    await this.canvasNotes.update(id, updates)
    const updated = (await this.canvasNotes.get(id)) ?? null
    if (updated) {
      await this.queueSyncChange('update', 'canvasNotes', { localId: id })
    }
    return updated
  }

  /**
   * Schreibt die GEMESSENE Höhe einer **Notiz** — ohne Sync-Eintrag UND ohne
   * die Sync-Buchführung der Zeile anzufassen.
   *
   * Der einzige Weg für eine **Notiz**, der bewusst an der Warteschlange
   * vorbeigeht. Ihre Höhe folgt dem Text und wird auf jedem Gerät neu
   * gemessen; sie ist eine Auskunft ÜBER die Darstellung, keine Entscheidung
   * des Nutzers — dieselbe Begründung, aus der der **Ausschnitt** lokal
   * bleibt (CONTEXT.md).
   *
   * Trüge der Sync sie mit, schrieben zwei Geräte mit leicht anderem
   * Schriftrendering abwechselnd 181 und 182 zurück, und die Notiz zappelte.
   *
   * Sie steht trotzdem IN der Zeile, weil **Einrasten**, **Maßklammern**,
   * **Randpfeile** und die **Lesereihenfolge** mit `width`/`height` rechnen und
   * nicht wissen dürfen, woher die Zahl kommt.
   *
   * **`withoutSyncBookkeeping`, nicht nur „kein `queueSyncChange`":** Ein
   * gewöhnliches `.update()` liefe trotzdem durch den Dexie-`updating`-Hook
   * (oben in der Datei), der bei JEDEM Schreiben `synced = false` setzt UND
   * `updatedAt`/`lastModified` auf „jetzt" vorrückt. Weil der Push die
   * Warteschlange liest und eine Messung dort nie einen Eintrag hinterlässt,
   * setzte das niemand mehr zurück — der `synced`-Teil wird zwar von einem
   * generischen Repair-Sweep in `pullRemoteEntities` (`sync-pull.ts`)
   * aufgeräumt, der vorgerückte `updatedAt` aber NICHT. Genau das ist der
   * eigentliche Schaden: Jede Fernänderung, die zeitlich vor der Messung
   * liegt, diesem Gerät aber noch nicht bekannt war, verliert danach
   * DAUERHAFT den LWW-Vergleich `remoteUpdated > updatedAt` gegen die
   * künstlich vorgerückte Zeit — nicht nur bis zum nächsten Sync-Zyklus.
   *
   * Eine Messung geht dabei selbst nie in die Cloud: Nur die Warteschlange
   * löst einen Push aus, und eine Messung stellt dort nie einen Eintrag. Ändert
   * der Nutzer später etwas anderes an derselben Notiz, fährt die zuletzt
   * gemessene Zahl in dieser einen (regulär eingereihten) Änderung mit.
   */
  async setCanvasNoteMeasuredHeight(id: string, height: number): Promise<void> {
    await withoutSyncBookkeeping(() => this.canvasNotes.update(id, { height }))
  }

  /**
   * Wie `setCanvasNoteMeasuredHeight`, für den **Text**: Bei ihm folgt auch die
   * Breite dem Inhalt — und ein zentrierter oder rechtsbündiger Text rückt dabei
   * sein `x` nach (`useMeasuredTextSize`). Auch das ohne eigenen Sync-Eintrag:
   * Es folgt allein aus der Breite und fährt mit der nächsten Änderung mit.
   */
  async setCanvasNoteMeasuredSize(id: string, size: { width: number; height: number; x?: number }): Promise<void> {
    await withoutSyncBookkeeping(() => this.canvasNotes.update(id, {
      width: size.width,
      height: size.height,
      ...(size.x === undefined ? {} : { x: size.x }),
    }))
  }

  async deleteCanvasNote(id: string): Promise<void> {
    const note = await this.canvasNotes.get(id)
    await this.canvasNotes.update(id, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'canvasNotes', { localId: id, cloudId: note?.cloudId })
  }

  // ---- Reward usage ("Zuletzt benutzt") ----

  /**
   * Records that a reward was picked. Idempotent by design: the row id is the
   * catalogue reward id, so picking the same reward again refreshes `usedAt`
   * instead of adding a row (see the v20 schema comment).
   */
  async recordRewardUsage(rewardId: string, userId: string | null = null): Promise<RewardUsage> {
    const now = new Date()
    const existing = await this.rewardUsages.get(rewardId)

    if (existing) {
      await this.rewardUsages.update(rewardId, { usedAt: now, deletedAt: null })
      await this.queueSyncChange('update', 'rewardUsages', { localId: rewardId })
      return { ...existing, usedAt: now, deletedAt: null }
    }

    const usage: RewardUsage = {
      id: rewardId,
      rewardId,
      usedAt: now,
      userId,
      cloudId: null,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
    }
    await this.rewardUsages.add(usage)
    await this.queueSyncChange('create', 'rewardUsages', { localId: usage.id })
    return usage
  }

  // ---- User preferences (synced key/value UI settings) ----

  /**
   * Reads a preference, or `defaultValue` when it was never set (or was
   * deleted). Never throws — a missing store must not take a view down.
   */
  async getPreference<T>(key: string, defaultValue: T): Promise<T> {
    const row = await this.userPreferences.get(key)
    if (!row || row.deletedAt) return defaultValue
    return row.value as T
  }

  /**
   * Writes a preference and queues it for cloud sync. Idempotent by design:
   * the row id is the key, so writing the same key again updates in place
   * instead of adding a row (see the v21 schema comment).
   *
   * Der Warteschlangen-Eintrag stößt den Sync selbst an (ADR 0017) — kein
   * Aufrufer ruft `scheduleSync()` von Hand.
   */
  async setPreference(key: string, value: unknown, userId: string | null = null): Promise<UserPreference> {
    const now = new Date()
    const existing = await this.userPreferences.get(key)

    if (existing) {
      await this.userPreferences.update(key, { value, deletedAt: null })
      await this.queueSyncChange('update', 'userPreferences', { localId: key })
      return { ...existing, value, deletedAt: null }
    }

    const preference: UserPreference = {
      id: key,
      key,
      value,
      userId,
      cloudId: null,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      lastModified: now,
      synced: false,
      syncedAt: null,
    }
    await this.userPreferences.add(preference)
    await this.queueSyncChange('create', 'userPreferences', { localId: preference.id })
    return preference
  }

  /** Soft-deletes a preference so the removal reaches the other devices. */
  async deletePreference(key: string): Promise<void> {
    const existing = await this.userPreferences.get(key)
    if (!existing) return
    await this.userPreferences.update(key, { deletedAt: new Date() })
    await this.queueSyncChange('delete', 'userPreferences', { localId: key, cloudId: existing.cloudId })
  }

  /** Most recently used rewards first — feeds the picker's top group. */
  async getRecentRewardUsages(limit = 5): Promise<RewardUsage[]> {
    const usages = await this.rewardUsages.filter(u => !u.deletedAt).toArray()
    return usages
      .sort((a, b) => new Date(b.usedAt).getTime() - new Date(a.usedAt).getTime())
      .slice(0, limit)
  }

  /**
   * Reiht eine Änderung für den Upload ein — **und stößt den Sync selbst an**.
   *
   * **Wer einreiht, will hochladen** (ADR 0017). Der Eintrag IST die
   * Ankündigung; keine Schreibstelle ruft `scheduleSync()` von Hand. Vorher tat
   * das jede Stelle für sich, und die 37 Schreibstellen des **Projekt-Canvas**
   * taten es nicht — eine Farbänderung lag deshalb bis zu fünf Minuten, bis
   * zufällig jemand anderes einen Sync auslöste.
   *
   * Angemeldet wird über `sync-notify` und nicht per direktem Import von
   * `scheduleSync`: `sync-engine.ts` importiert `db` von hier, ein Gegenimport
   * wäre zirkulär.
   *
   * `silent` ist für die Einreihungen, die der Sync **selbst** vornimmt
   * (`stageUnsyncedWithNameDedup`, `stageOrphanedLocalRecords`). Beide laufen
   * vor `getPendingChanges()` und sind vom laufenden Zyklus ohnehin gedeckt;
   * ohne die Ausnahme stieße jeder Sync den nächsten an.
   */
  async queueSyncChange(
    action: 'create' | 'update' | 'delete',
    table: SyncQueueItem['table'],
    data: any,
    options?: { silent?: boolean }
  ): Promise<void> {
    try {
      await this.syncQueue.add({
        action,
        table,
        recordId: 0,
        data,
        synced: false,
        timestamp: new Date(),
      })
    } catch (error) {
      console.error('[SyncQueue] Failed to queue change:', error)
      return
    }
    if (!options?.silent) notifySyncQueued()
  }

  // Utility methods
  async clearAllData(): Promise<void> {
    await this.transaction('rw', [this.tasks, this.sections, this.settings, this.userProfiles, this.timeEntries, this.taskComments, this.projectComments, this.taskReminders, this.tags, this.projects, this.goals, this.habits, this.habitLogs, this.rewardUsages, this.userPreferences, this.canvasNotes, this.syncMetadata, this.syncQueue, this.avatarCategories, this.avatars], () => {
      this.tasks.clear()
      this.sections.clear()
      this.settings.clear()
      this.userProfiles.clear()
      this.timeEntries.clear()
      this.taskComments.clear()
      this.projectComments.clear()
      this.taskReminders.clear()
      this.tags.clear()
      this.projects.clear()
      this.goals.clear()
      this.habits.clear()
      this.habitLogs.clear()
      this.rewardUsages.clear()
      this.userPreferences.clear()
      this.canvasNotes.clear()
      this.syncMetadata.clear()
      this.syncQueue.clear()
      this.avatarCategories.clear()
      this.avatars.clear()
    })
  }

  async markAsSynced(tableName: string, id: string): Promise<void> {
    const now = new Date()
    const table = (this as any)[tableName] as Table
    if (table) {
      await table.update(id, { 
        synced: true, 
        syncedAt: now 
      })
    }
  }
}

// Create singleton instance
export const db = new HappyTaskDB()

// ── Dev-only: Schreibvorgänge pro Tabelle zählen ────────────────────────────
//
// Anlass: Montierte Projekt-Tabs rendern dauerhaft neu (16–85 ms je Durchgang,
// auch inaktive), ohne Nutzeraktion und nachdem die Sync-Warteschlange bereits
// aufgegeben hat. `useLiveQuery` feuert bei jedem Schreibvorgang in ALLEN
// montierten Tabs — unbekannt ist bloß, WER fortwährend schreibt.
//
// Diese Zähler beantworten genau das. Sticht eine Tabelle heraus, sitzt dort
// der Schreiber. Bleiben alle bei null und der Sturm läuft trotzdem, ist es
// kein Dexie-Problem, sondern React-Zustand — dann übernimmt der
// DevTools-Profiler.
//
// Siehe .claude/tasks/tab-render-storm.md. In Produktionsbauten fällt der
// ganze Block per `process.env.NODE_ENV` weg (wie lib/perf/tabProfiling.ts).
if (process.env.NODE_ENV !== 'production') {
  const writes = new Map<string, { create: number; update: number; remove: number }>()

  const bump = (table: string, kind: 'create' | 'update' | 'remove') => {
    let row = writes.get(table)
    if (!row) {
      row = { create: 0, update: 0, remove: 0 }
      writes.set(table, row)
    }
    row[kind] += 1
  }

  for (const table of db.tables) {
    table.hook('creating', () => { bump(table.name, 'create') })
    table.hook('updating', () => { bump(table.name, 'update') })
    table.hook('deleting', () => { bump(table.name, 'remove') })
  }

  setInterval(() => {
    if (writes.size === 0) return
    const line = [...writes.entries()]
      .map(([name, r]) => `${name} ${r.create}c/${r.update}u/${r.remove}d`)
      .join('  ')
    writes.clear()
    console.warn(`[DB/writes] ${line}`)
  }, 5000)
}